import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LayoutDashboard, FolderTree, ListTree, Megaphone, 
  Link as LinkIcon, Users, CalendarCheck, CreditCard, 
  BookOpen, LogOut, Lock, Mail, Key, Settings, Edit, Trash2, X,
  Upload, Download, FileText, UserCog, Phone, TrendingUp, Smartphone, Monitor,
  Search, ChevronRight, ChevronDown, Zap, FileSpreadsheet, Plus
} from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import html2pdf from 'html2pdf.js';
import { ensureGujaratiFontReady } from '../utils/gujFont';
import SubCategoryFormEditor from '../components/SubCategoryFormEditor';
import FormMaster from '../components/FormMaster';
import SiteSettings from '../components/SiteSettings';
import ManageBlogs from '../components/ManageBlogs';
import BulkEmailManager from '../components/BulkEmailManager';
import { AdminPagination } from '../components/AdminPagination';
import { GreenCardVisual, downloadCardPng, printCardAtAadhaarSize } from '../components/GreenCardVisual';

import * as XLSX from 'xlsx';

// Fallback quick-pick Gujarati remark templates used until admin-configured
// templates load from /api/settings. Admins can override these from Site Settings.
const REMARK_TEMPLATES = [
  'દસ્તાવેજો પ્રાપ્ત થયા',
  'ચકાસણી ચાલુ છે',
  'વધારાના દસ્તાવેજો જરૂરી છે',
  'ચુકવણી બાકી છે',
  'કચેરીમાં સબમિટ થયું',
  'મંજૂરી માટે મોકલ્યું',
  'તૈયાર છે - લેવા આવો',
  'પૂર્ણ થયું, ડિલિવરી કરી',
];

export default function AdminPanel() {
  const [token, setToken] = useState(() => localStorage.getItem('adminToken'));
  const [activeTab, setActiveTab] = useState('dashboard');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Edit Modal States
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubCategory, setEditingSubCategory] = useState(null);

  // Login State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Data States
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [links, setLinks] = useState([]);
  const [applications, setApplications] = useState([]);
  const [appPage, setAppPage] = useState(1);
  const [appTotalCount, setAppTotalCount] = useState(0);
  const [appStats, setAppStats] = useState({ Total: 0, Drafts: 0, by_status: {}, exec_workload: {} });
  const [appTypeCounts, setAppTypeCounts] = useState({ all: 0, urgent: 0, normal: 0 });
  const APP_PAGE_SIZE = 50;
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [cities, setCities] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [execForm, setExecForm] = useState({ name: '', designation: '', contact_number: '' });
  const [editingExec, setEditingExec] = useState(null);
  const [assignExecModal, setAssignExecModal] = useState(null); // { appNumber, currentExecId }
  const [assignSelectedExec, setAssignSelectedExec] = useState('');
  const [accountsMonthFilter, setAccountsMonthFilter] = useState(''); // YYYY-MM
  const [insightsRange, setInsightsRange] = useState('7d');
  const [insightsData, setInsightsData] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [visitorsToday, setVisitorsToday] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [usersSortDir, setUsersSortDir] = useState('desc'); // 'desc' = newest first
  const [usersSearch, setUsersSearch] = useState('');
  const [bookingPhoneFilter, setBookingPhoneFilter] = useState(''); // drill-down from Users → Bookings
  const [recentActivity, setRecentActivity] = useState([]);
  // Inline remark editing – map of application_number -> draft text so admins can
  // type freely without triggering a network call on every keystroke. The value
  // is committed on blur / Enter.
  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [remarkSavingKey, setRemarkSavingKey] = useState(null);
  // Which row currently has the quick-pick template popover open (app_number or null)
  const [remarkTemplateOpenFor, setRemarkTemplateOpenFor] = useState(null);
  // Admin-configurable list from /api/settings (falls back to REMARK_TEMPLATES)
  const [remarkTemplates, setRemarkTemplates] = useState(REMARK_TEMPLATES);

  // Form States
  const [catForm, setCatForm] = useState({ title: '', slug: '', icon_type: 'file', discount_percentage: 0 });
  const [subCatForm, setSubCatForm] = useState({ 
    category_name: '', title: '', slug: '', icon_type: 'FileText', fee_amount: 50,
    estimated_days: '3-5 દિવસ', normal_enabled: true, tatkal_enabled: false, tatkal_fee_amount: 150,
    important_note: ''
  });
  const [cityForm, setCityForm] = useState({ name: '' });
  const [annForm, setAnnForm] = useState({ text: '' });
  const [linkForm, setLinkForm] = useState({ text: '', url: '#' });
  const [expandedSubCat, setExpandedSubCat] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const [showFieldsSummary, setShowFieldsSummary] = useState(null);
  const [uploadingApp, setUploadingApp] = useState(null);
  const [bookingTypeFilter, setBookingTypeFilter] = useState('all'); // 'all' | 'urgent' | 'normal'
  const [bookingStatusFilter, setBookingStatusFilter] = useState('all'); // 'all' | 'Submitted' | 'Pending' | 'Inprocess' | 'Complete'
  const [bookingDateFrom, setBookingDateFrom] = useState('');
  const [bookingSearch, setBookingSearch] = useState('');
  const [debouncedBookingSearch, setDebouncedBookingSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBookingSearch(bookingSearch), 300);
    return () => clearTimeout(t);
  }, [bookingSearch]);
  const [bookingDateTo, setBookingDateTo] = useState('');
  const [greencards, setGreencards] = useState([]);
  const [greencardSettings, setGreencardSettings] = useState(null);
  const [adminVisits, setAdminVisits] = useState([]);
  const [newBenefit, setNewBenefit] = useState('');
  const [gcViewCard, setGcViewCard] = useState(null);
  const [gcEditCard, setGcEditCard] = useState(null);
  const [gcEditSaving, setGcEditSaving] = useState(false);
  const [gcSearch, setGcSearch] = useState('');
  const [gcStatusFilter, setGcStatusFilter] = useState('all');
  const [gcCourierFilter, setGcCourierFilter] = useState('all');
  const [gcDownloadCard, setGcDownloadCard] = useState(null);
  const [gcSelected, setGcSelected] = useState({});
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const handleBulkLabels = async (all) => {
    const selected = all ? gcFiltered.map(c => c.card_number) : gcFiltered.filter(c => gcSelected[c.card_number]).map(c => c.card_number);
    if (selected.length === 0) return toast.error('કોઈ કાર્ડ પસંદ કર્યું નથી');
    setBulkDownloading(true);
    toast.info('લેબલ ડાઉનલોડ થઈ રહ્યા છે...');
    try {
      const res = await axios.post(`${API_URL}/api/admin/greencards/bulk-labels`, { card_numbers: selected }, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `DeliveryLabels_${all ? 'All' : 'Selected'}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
    } catch(e) { toast.error('ડાઉનલોડ નિષ્ફળ'); }
    finally { setBulkDownloading(false); }
  };

  const handleBulkCards = async (all) => {
    const selected = all ? gcFiltered.map(c => c.card_number) : gcFiltered.filter(c => gcSelected[c.card_number]).map(c => c.card_number);
    if (selected.length === 0) return toast.error('કોઈ કાર્ડ પસંદ કર્યું નથી');
    setBulkDownloading(true);
    toast.info('કાર્ડ્સ ડાઉનલોડ થઈ રહ્યા છે...');
    try {
      const res = await axios.post(`${API_URL}/api/admin/greencards/bulk-cards`, { card_numbers: selected }, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `GreenCards_${all ? 'All' : 'Selected'}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
    } catch(e) { toast.error('ડાઉનલોડ નિષ્ફળ'); }
    finally { setBulkDownloading(false); }
  };
  const gcViewRef = useRef(null);
  const gcDownloadRef = useRef(null);
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', tag: 'Manual', date: '' });
  const uploadInputRef = useRef(null);

  const MAX_UPLOAD_MB = 10;
  const ALLOWED_UPLOAD_TYPES = [
    'application/pdf',
    'image/png', 'image/jpeg', 'image/webp',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];
  const ALLOWED_EXTS = ['pdf', 'png', 'jpg', 'jpeg', 'webp', 'doc', 'docx'];

  const handleUploadProcessedDoc = async (appNumber, file) => {
    if (!file) return;

    // Type guard — strict validation based on extension
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const isImage = ALLOWED_EXTS.includes(ext) && ext !== 'pdf';
    const isPDF = ext === 'pdf';
    
    // Strict separation: Images go to Image fields, PDFs go to PDF fields (this assumes backend knows context, but we are doing a generic upload here, so we just check global limits)
    // However, user specifically asked for error message if JPG uploaded to PDF requirement
    // Since this is a generic processed doc upload (not mapped to a specific requirement field), we allow both but enforce size and overall type
    const typeOk = ALLOWED_UPLOAD_TYPES.includes(file.type) || ALLOWED_EXTS.includes(ext);
    
    if (!typeOk) {
      toast.error(`માન્ય ફોર્મેટ નથી. ફક્ત ${ALLOWED_EXTS.join(', ').toUpperCase()} ને મંજૂરી છે.`);
      return;
    }

    // Since this is Admin upload, limit is fixed at 5MB as requested (previously used MAX_UPLOAD_MB)
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > 5) {
      toast.error(`ફાઈલ ખૂબ મોટી છે (${sizeMb.toFixed(1)} MB). મહત્તમ 5 MB.`);
      return;
    }

    setUploadingApp(appNumber);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(
        `${API_URL}/api/admin/applications/${appNumber}/processed-docs`,
        form,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('દસ્તાવેજ અપલોડ થયો');
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail || 'અપલોડ નિષ્ફળ';
      toast.error(String(detail).slice(0, 120));
    } finally {
      setUploadingApp(null);
    }
  };

  const handleDeleteProcessedDoc = async (appNumber, docId) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/applications/${appNumber}/processed-docs/${docId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('દસ્તાવેજ કાઢી નાખ્યો');
      fetchData();
    } catch (e) {
      toast.error('દૂર કરવામાં નિષ્ફળ');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/admin/login`, { email, password });
      const t = res.data.token;
      setToken(t);
      localStorage.setItem('adminToken', t);
      toast.success('લૉગિન સફળ (Login Successful)');
    } catch (err) {
      toast.error('અમાન્ય ઇમેઇલ અથવા પાસવર્ડ (Invalid credentials)');
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('adminToken');
    toast.success('લૉગઆઉટ સફળ (Logout Successful)');
  };

  const appQueryParams = (extra = {}) => {
    const params = { status: bookingStatusFilter, app_type: bookingTypeFilter, ...extra };
    if (bookingPhoneFilter) params.phone = bookingPhoneFilter;
    if (bookingDateFrom) params.date_from = bookingDateFrom;
    if (bookingDateTo) params.date_to = bookingDateTo;
    if (debouncedBookingSearch) params.search = debouncedBookingSearch;
    return params;
  };

  const appReqId = useRef(0);
  const fetchApplications = async () => {
    if (!token) return;
    const reqId = ++appReqId.current;
    try {
      const res = await axios.get(`${API_URL}/api/admin/applications`, {
        params: appQueryParams({ skip: (appPage - 1) * APP_PAGE_SIZE, limit: APP_PAGE_SIZE }),
        headers: { Authorization: `Bearer ${token}` },
      });
      if (reqId !== appReqId.current) return;
      setApplications(res.data.items || []);
      setAppTotalCount(res.data.total || 0);
      setAppTypeCounts(res.data.type_counts || { all: 0, urgent: 0, normal: 0 });
      setAppStats(res.data.stats);
    } catch (e) {
      toast.error('અરજીઓ લોડ કરવામાં નિષ્ફળ (Failed to load applications)');
    }
  };

  const fetchAllFilteredApps = async (extra = {}) => {
    const res = await axios.get(`${API_URL}/api/admin/applications`, {
      params: appQueryParams({ skip: 0, limit: 0, ...extra }),
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.items || [];
  };

  const fetchData = async () => {
    if (!token) return;
    try {
        fetchApplications();
        const [catRes, subRes, annRes, linkRes, userRes, accRes, cityRes, execRes, gcSettingsRes, gcRes, visitsRes] = await Promise.all([
          axios.get(`${API_URL}/api/services`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/subservices`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/announcements`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/links`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/admin/accounts`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/cities`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/admin/executives`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/greencard/settings`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/admin/greencards`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/admin/visits`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setCategories(catRes.data);
        if (catRes.data.length > 0 && !subCatForm.category_name) {
          setSubCatForm(p => ({ ...p, category_name: catRes.data[0].title }));
        }
        setSubCategories(subRes.data);
        setAnnouncements(annRes.data);
        setLinks(linkRes.data);
        setUsers(userRes.data);
      setAccounts(accRes.data);
      setCities(cityRes.data);
      setExecutives(Array.isArray(execRes.data) ? execRes.data : []);
      setGreencardSettings(gcSettingsRes.data);
      setGreencards(gcRes.data);
      setAdminVisits(visitsRes.data);
    } catch (e) {
      toast.error('Data fetch failed');
    }
  };

  const handleToggleFeature = async (id, currentStatus) => {
    try {
      await axios.patch(`${API_URL}/api/subservices/${id}/feature`, { is_featured: !currentStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Feature status updated');
      fetchData();
    } catch (e) {
      toast.error('Failed to update feature status');
    }
  };

  const handleAddExpense = async () => {
    if (!expenseForm.description.trim() || !expenseForm.amount) {
      toast.error('વર્ણન અને રકમ ફરજિયાત છે');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/accounts/expense`, {
        description: expenseForm.description,
        amount: Number(expenseForm.amount),
        tag: expenseForm.tag || 'Manual',
        date: expenseForm.date || undefined
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('ખર્ચ ઉમેર્યું (Expense added)');
      setExpenseForm({ description: '', amount: '', tag: 'Manual', date: '' });
      fetchData();
    } catch (e) {
      toast.error('ખર્ચ ઉમેરવામાં નિષ્ફળ');
    }
  };

  const handleDeleteAccountEntry = async (id) => {
    if (!window.confirm('શું તમે આ એન્ટ્રી કાઢી નાખવા માંગો છો?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/accounts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('એન્ટ્રી કાઢી નાખી');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleStatusUpdate = async (appNumber, newStatus, executiveId = null, overrideRemark = null) => {
    // If moving to Inprocess and no executive selected yet -> open assignment modal
    if (newStatus === 'Inprocess' && !executiveId) {
      if (!Array.isArray(executives) || executives.length === 0) {
        toast.error('પ્રથમ "Manage Executives" ટૅબમાં એક્ઝિક્યુટિવ ઉમેરો');
        return;
      }
      const existingApp = (applications || []).find(a => a.application_number === appNumber);
      setAssignSelectedExec(existingApp?.assigned_executive?.id || executives[0]?.id || '');
      setAssignExecModal({ appNumber, currentExecId: existingApp?.assigned_executive?.id || null });
      return;
    }
    
    let adminRemark = overrideRemark;
    if (adminRemark === null) {
      const confirmMsg = `Are you sure you want to move this application to '${newStatus}'?`;
      if (!window.confirm(confirmMsg)) return;
      adminRemark = prompt('રીમાર્ક (Admin Remark):', '') || '';
    }

    try {
      const payload = { status: newStatus, admin_remark: adminRemark };
      if (executiveId) payload.assigned_executive_id = executiveId;
      await axios.patch(`${API_URL}/api/admin/applications/${appNumber}/status`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated successfully');
      fetchData();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const confirmAssignExecutive = async () => {
    if (!assignSelectedExec) {
      toast.error('એક્ઝિક્યુટિવ પસંદ કરો');
      return;
    }
    const { appNumber } = assignExecModal;
    setAssignExecModal(null);
    const remark = prompt('રીમાર્ક (Admin Remark):', '') || '';
    await handleStatusUpdate(appNumber, 'Inprocess', assignSelectedExec, remark);
  };

  // Save a remark inline without changing the application status. Called on
  // blur / Enter from the remark input in the Booking Management table.
  const saveRemark = async (appNumber, newRemark, existingRemark) => {
    const trimmed = (newRemark || '').trim();
    const previous = (existingRemark || '').trim();
    if (trimmed === previous) return; // no-op
    setRemarkSavingKey(appNumber);
    try {
      await axios.patch(
        `${API_URL}/api/admin/applications/${appNumber}/remark`,
        { remark: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('રીમાર્ક સેવ થયું');
      setRemarkDrafts(prev => {
        const next = { ...prev };
        delete next[appNumber];
        return next;
      });
      fetchData();
    } catch (e) {
      toast.error('રીમાર્ક સેવ કરવામાં નિષ્ફળ');
    } finally {
      setRemarkSavingKey(null);
    }
  };


  const handleAddOrUpdateExec = async () => {
    if (!execForm.name.trim() || !execForm.designation.trim() || !execForm.contact_number.trim()) {
      toast.error('બધા ફિલ્ડ ફરજિયાત છે');
      return;
    }
    try {
      if (editingExec) {
        await axios.patch(`${API_URL}/api/admin/executives/${editingExec.id}`, execForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('એક્ઝિક્યુટિવ અપડેટ થયું');
      } else {
        await axios.post(`${API_URL}/api/admin/executives`, execForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('એક્ઝિક્યુટિવ ઉમેર્યું');
      }
      setExecForm({ name: '', designation: '', contact_number: '' });
      setEditingExec(null);
      fetchData();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const handleDeleteExec = async (id) => {
    if (!window.confirm('શું તમે આ એક્ઝિક્યુટિવને કાઢી નાખવા માંગો છો?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/executives/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('કાઢી નાખ્યું');
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const startEditExec = (ex) => {
    setEditingExec(ex);
    setExecForm({ name: ex.name || '', designation: ex.designation || '', contact_number: ex.contact_number || '' });
  };

  // --- Insights ---
  const fetchInsights = async (rng) => {
    setInsightsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/insights?range=${rng}`, { headers: { Authorization: `Bearer ${token}` } });
      setInsightsData(res.data);
    } catch (e) {
      toast.error('Insights ડેટા લોડ કરવામાં નિષ્ફળ');
    } finally {
      setInsightsLoading(false);
    }
  };

  // Load admin-configurable remark templates from settings, and keep them in
  // sync when the admin saves changes in Site Settings.
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (Array.isArray(res.data?.remark_templates) && res.data.remark_templates.length > 0) {
          setRemarkTemplates(res.data.remark_templates);
        }
      } catch (e) { /* keep fallback */ }
    };
    load();
    const handler = (e) => {
      const list = e?.detail?.remark_templates;
      if (Array.isArray(list) && list.length > 0) setRemarkTemplates(list);
    };
    window.addEventListener('siteSettingsUpdated', handler);
    return () => window.removeEventListener('siteSettingsUpdated', handler);
  }, [API_URL]);

  useEffect(() => {
    if ((activeTab === 'insights' || activeTab === 'dashboard') && token) {
      fetchInsights(insightsRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, insightsRange, token]);

  // Live "Visitors Today" tile on Dashboard — refresh whenever admin lands on Dashboard.
  useEffect(() => {
    const loadTodayCount = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/insights/today-count`, { headers: { Authorization: `Bearer ${token}` } });
        setVisitorsToday(res.data.total || 0);
      } catch (e) { /* ignore */ }
    };
    const loadRecentActivity = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/recent-activity?limit=10`, { headers: { Authorization: `Bearer ${token}` } });
        setRecentActivity(Array.isArray(res.data) ? res.data : []);
      } catch (e) { /* ignore */ }
    };
    if (activeTab === 'dashboard' && token) {
      loadTodayCount();
      loadRecentActivity();
      const iv = setInterval(() => { loadTodayCount(); loadRecentActivity(); }, 30000); // refresh every 30s
      return () => clearInterval(iv);
    }
  }, [activeTab, token, API_URL]);

  const handleUpdateOrder = async (id, newOrder) => {
    try {
      await axios.patch(`${API_URL}/api/subservices/${id}/order`, { order_num: Number(newOrder) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Order updated');
      fetchData();
    } catch (e) {
      toast.error('Failed to update order');
    }
  };

  const handleUpdateMainOrder = async (id, newOrder) => {
    try {
      await axios.patch(`${API_URL}/api/services/${id}/order`, { order_num: Number(newOrder) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Main category order updated');
      fetchData();
    } catch (e) {
      toast.error('Failed to update main category order');
    }
  };
  const handleEditCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/services/${editingCategory.id}`, editingCategory, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Category updated successfully');
      setEditingCategory(null);
      fetchData();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const handleEditSubCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/subservices/${editingSubCategory.id}`, editingSubCategory, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Sub Category updated successfully');
      setEditingSubCategory(null);
      fetchData();
    } catch (e) {
      toast.error('Update failed');
    }
  };


  useEffect(() => {
    fetchData();
  }, [token, API_URL]);

  useEffect(() => {
    setAppPage(1);
  }, [bookingStatusFilter, bookingTypeFilter, bookingPhoneFilter, bookingDateFrom, bookingDateTo, debouncedBookingSearch]);

  useEffect(() => {
    fetchApplications();
  }, [token, appPage, bookingStatusFilter, bookingTypeFilter, bookingPhoneFilter, bookingDateFrom, bookingDateTo, debouncedBookingSearch]);

  useEffect(() => {
    if (!gcDownloadCard) return;
    const t = setTimeout(() => {
      downloadCardPng(gcDownloadRef.current, gcDownloadCard.card_number)
        .then(() => toast.success('કાર્ડ ડાઉનલોડ થઈ ગયું (Card downloaded)'))
        .catch(() => toast.error('કાર્ડ ડાઉનલોડ કરવામાં ભૂલ'))
        .finally(() => setGcDownloadCard(null));
    }, 300);
    return () => clearTimeout(t);
  }, [gcDownloadCard]);

  const handleDelete = async (endpoint, id) => {
    try {
      await axios.delete(`${API_URL}/api/${endpoint}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted successfully');
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleCreate = async (endpoint, data) => {
    try {
      await axios.post(`${API_URL}/api/${endpoint}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Created successfully');
      fetchData();
    } catch (e) {
      toast.error('Create failed');
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center font-sans">
        <div className="bg-white p-10 rounded-xl shadow-xl w-full max-w-md border-t-4 border-[#172554]">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-[#172554] rounded-full flex items-center justify-center">
              <Lock className="text-white w-8 h-8" />
            </div>
          </div>
          <h2 className="text-center text-2xl font-bold text-[#172554] mb-8 text-gujarati">એડમિન પેનલ લોગિન</h2>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#172033] mb-1">Email ID</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input 
                  type="email" required
                  className="w-full pl-10 pr-4 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#172554] focus:border-transparent outline-none"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="kagajbook@gmail.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#172033] mb-1">Password</label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input 
                  type="password" required
                  className="w-full pl-10 pr-4 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#172554] focus:border-transparent outline-none"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            </div>
            <button type="submit" className="w-full bg-[#F5B800] text-[#172554] py-3 rounded-lg font-bold hover:bg-[#FFC928] transition-colors">
              Login (લોગિન)
            </button>
          </form>
        </div>
      </div>
    );
  }

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'categories', label: 'Main Categories', icon: FolderTree },
    { id: 'subcategories', label: 'Sub Categories', icon: ListTree },
    { id: 'form_master', label: 'Form Master', icon: Settings },
    { id: 'cities', label: 'City Management', icon: FolderTree },
    { id: 'announcements', label: 'Announcements', icon: Megaphone },
    { id: 'links', label: 'Important Links', icon: LinkIcon },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'settings', label: 'Site Settings', icon: Settings },
    { id: 'greencards', label: 'Green Cards', icon: FolderTree },
    { id: 'visits', label: 'Visit Allocations', icon: CalendarCheck },
    { id: 'bookings', label: 'Booking Management', icon: CalendarCheck },
    { id: 'executives', label: 'Manage Executives', icon: UserCog },
    { id: 'accounts', label: 'Account Book', icon: BookOpen },
    { id: 'blogs', label: 'Manage Blogs', icon: FileText },
    { id: 'bulk_emails', label: 'Bulk Email Manager', icon: Mail },
    { id: 'insights', label: 'Insights', icon: TrendingUp },
  ];

  // Helper for Exporting Data
  const handleSendDraftReminder = async (appIds) => {
    if (!token) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/applications/draft-reminder`,
        { application_ids: appIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`રિમાઇન્ડર ${res.data.sent} અરજદારોને મોકલવામાં આવ્યું (Sent ${res.data.sent})`);
    } catch (e) {
      console.error(e);
      toast.error('રિમાઇન્ડર મોકલવામાં નિષ્ફળ (Failed to send reminder)');
    }
  };
  const handleExportExcel = (type, dataList) => {
    if (!dataList || dataList.length === 0) {
      toast.error('કોઈ ડેટા ઉપલબ્ધ નથી (No data available)');
      return;
    }
    try {
      const ws = XLSX.utils.json_to_sheet(dataList);
      
      // Auto-fit column widths
      const colWidths = [];
      const keys = Object.keys(dataList[0]);
      keys.forEach((key, idx) => {
        let max = key.length;
        dataList.forEach(row => {
          const val = String(row[key] || '');
          if (val.length > max) max = val.length;
        });
        colWidths[idx] = { wch: Math.min(max + 2, 50) };
      });
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Export");
      XLSX.writeFile(wb, `Kagajbook_${type}_Export.xlsx`);
      toast.success('એક્સલ ફાઈલ ડાઉનલોડ થઈ ગઈ (Excel Downloaded)');
    } catch (e) {
      console.error(e);
      toast.error('એક્સપોર્ટ કરવામાં ભૂલ આવી (Export failed)');
    }
  };

  const handleExportPDF = async (type, dataList, columns) => {
    if (!dataList || dataList.length === 0) {
      toast.error('કોઈ ડેટા ઉપલબ્ધ નથી (No data available)');
      return;
    }
    try {
      const NAVY = '#172554';
      const YELLOW = '#F5B800';
      const now = new Date();
      const generatedAt = now.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

      // Booking-specific column set: User, Service, Mobile, Status
      const isBooking = type === 'Bookings';
      const bookingRow = (a) => {
        const isUrgent = (a.application_type || '').includes('Urgent') || (a.application_type || '').includes('તત્કાલ');
        const statusMap = {
          'Submitted': 'સબમિટ (Submitted)',
          'Pending': 'પેન્ડિંગ (Pending)',
          'Inprocess': 'પ્રક્રિયા ચાલુ (In Process)',
          'Complete': 'પૂર્ણ (Complete)',
          'Cancelled': 'રદ / રિફંડ (Cancelled)',
        };
        const statusColor = a.status === 'Complete' ? '#166534' : a.status === 'Cancelled' ? '#991b1b' : (a.status === 'Inprocess' ? '#a16207' : '#1e40af');
        const statusBg = a.status === 'Complete' ? '#dcfce7' : a.status === 'Cancelled' ? '#fee2e2' : (a.status === 'Inprocess' ? '#fef9c3' : '#dbeafe');
        return `
          <tr>
            <td>
              <div style="font-weight:700; color:${NAVY}; font-size:11px;">${a.application_number || ''}</div>
              <div style="font-weight:600; font-size:11px;">${a.full_name || '-'}</div>
              <div style="color:#64748B; font-size:10px;">${a.city || ''}</div>
            </td>
            <td>
              <div style="font-weight:600; font-size:11px;">${a.sub_category_name || '-'}</div>
              <div style="color:#64748B; font-size:10px;">${a.category_name || ''}</div>
              ${isUrgent 
                ? `<span style="display:inline-block; margin-top:3px; background:${YELLOW}; color:${NAVY}; font-weight:800; padding:1px 6px; border-radius:999px; font-size:9px;">⚡ તત્કાલ</span>`
                : `<span style="display:inline-block; margin-top:3px; background:#dbeafe; color:#1e40af; font-weight:700; padding:1px 6px; border-radius:999px; font-size:9px;">સામાન્ય</span>`
              }
            </td>
            <td style="font-family: monospace; font-size:11px;">${a.user_phone || '-'}</td>
            <td>
              <span style="display:inline-block; background:${statusBg}; color:${statusColor}; font-weight:700; padding:3px 8px; border-radius:6px; font-size:10px;">
                ${statusMap[a.status] || a.status || '-'}
              </span>
              <div style="color:#64748B; font-size:9px; margin-top:4px;">
                ${a.created_at ? new Date(a.created_at).toLocaleDateString('en-IN') : ''}
              </div>
            </td>
          </tr>
        `;
      };

      const genericRow = (item) => `
        <tr>
          ${columns.map(col => {
            let val = col.accessor ? col.accessor(item) : item[col.key];
            if (typeof val === 'object' && val !== null) val = JSON.stringify(val);
            if (col.key === 'created_at' && val) val = new Date(val).toLocaleDateString('en-IN');
            return `<td style="font-size:11px;">${val != null && val !== '' ? String(val) : '-'}</td>`;
          }).join('')}
        </tr>
      `;

      const headers = isBooking 
        ? ['User (અરજદાર)', 'Service (સેવા)', 'Mobile', 'Status (સ્થિતિ)']
        : columns.map(c => c.label);

      const rowsHtml = isBooking 
        ? dataList.map(bookingRow).join('')
        : dataList.map(genericRow).join('');

      const html = `
        <div style="font-family:'Noto Sans Gujarati','Inter',sans-serif; padding:20px; color:#0f172a; background:#ffffff;">
          <!-- Brand Header -->
          <div style="text-align:center; padding-bottom:14px; border-bottom:3px solid ${YELLOW};">
            <div style="color:${NAVY}; font-size:26px; font-weight:800; letter-spacing:0.5px;">કાગજબુક ઓનલાઈન</div>
            <div style="color:#64748B; font-size:11px; margin-top:2px; letter-spacing:2px; text-transform:uppercase;">Kagajbook Online · Gujarat Documentation Portal</div>
          </div>

          <!-- Report Meta -->
          <div style="display:flex; justify-content:space-between; align-items:center; margin:14px 0 10px 0;">
            <div>
              <div style="color:${NAVY}; font-weight:800; font-size:16px;">${isBooking ? 'Booking Management Report' : type + ' Report'}</div>
              <div style="color:#64748B; font-size:10px; margin-top:2px;">${isBooking ? 'અરજી વ્યવસ્થાપન અહેવાલ' : ''}</div>
            </div>
            <div style="text-align:right;">
              <div style="color:#64748B; font-size:10px;">Generated on</div>
              <div style="color:${NAVY}; font-weight:700; font-size:11px;">${generatedAt}</div>
            </div>
          </div>

          <!-- Data Table -->
          <table style="width:100%; border-collapse:collapse; margin-top:8px;">
            <thead>
              <tr style="background:${NAVY}; color:#ffffff;">
                ${headers.map((h, i) => `
                  <th style="padding:10px 8px; text-align:left; font-size:11px; font-weight:700; border-bottom:3px solid ${YELLOW}; ${i === 0 ? 'border-top-left-radius:6px;' : ''} ${i === headers.length - 1 ? 'border-top-right-radius:6px;' : ''}">
                    ${h}
                  </th>
                `).join('')}
              </tr>
            </thead>
            <tbody>
              ${rowsHtml.replace(/<tr>/g, (m, o) => `<tr style="background:#ffffff; border-bottom:1px solid #e2e8f0;">`)}
            </tbody>
          </table>

          <!-- Footer Summary -->
          <div style="margin-top:16px; padding-top:12px; border-top:2px solid ${NAVY}; display:flex; justify-content:space-between; align-items:center;">
            <div style="color:${NAVY}; font-weight:700; font-size:12px;">
              કુલ અહેવાલ (Total Records): <span style="background:${YELLOW}; color:${NAVY}; padding:2px 8px; border-radius:6px; margin-left:4px;">${dataList.length}</span>
            </div>
            <div style="color:#64748B; font-size:10px;">
              Generated at ${generatedAt} · Confidential
            </div>
          </div>
          <div style="text-align:center; margin-top:8px; color:#94a3b8; font-size:9px;">
            © ${now.getFullYear()} Kagajbook Online — All rights reserved
          </div>
        </div>
      `;

      // Alternating row colors via inline style embedded into the string
      let idxCounter = 0;
      const zebraHtml = html.replace(/<tr>\s*(<td)/g, () => {
        const bg = idxCounter % 2 === 0 ? '#ffffff' : '#f8fafc';
        idxCounter += 1;
        return `<tr style="background:${bg}; border-bottom:1px solid #e2e8f0;"><td style="padding:8px; vertical-align:top;"`;
      });
      const finalHtml = zebraHtml.replace(/<td([^>]*)>/g, (m, attrs) => {
        // Only pad TDs inside tbody rows we haven't already patched
        if (attrs.includes('padding')) return m;
        return `<td${attrs} style="padding:8px; vertical-align:top;">`;
      });

      // A4 Landscape usable area ≈ 10.5" wide minus 0.8" margins = 9.7" ≈ 930px @ 96dpi
      const CANVAS_WIDTH = 930;

      const opt = {
        margin:       [0.4, 0.4, 0.4, 0.4],
        filename:     `Kagajbook_${type}_${now.toISOString().slice(0,10)}.pdf`,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: CANVAS_WIDTH },
        jsPDF:        { unit: 'in', format: 'a4', orientation: 'landscape' },
        pagebreak:    { mode: ['avoid-all', 'css', 'legacy'] }
      };

      // Wait for Noto Sans Gujarati (bundled) to load before rasterizing
      await ensureGujaratiFontReady();
      const ensureFont = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();

      ensureFont.then(() => new Promise(r => setTimeout(r, 300))).then(() => {
        // Use string mode — html2pdf handles internal hidden container rendering itself
        const wrappedString = `<div style="width:${CANVAS_WIDTH}px; background:#ffffff;">${finalHtml}</div>`;
        html2pdf().from(wrappedString, 'string').set(opt).save().then(() => {
          toast.success('PDF ફાઈલ ડાઉનલોડ થઈ ગઈ (PDF Downloaded)');
        }).catch(err => {
          console.error(err);
          toast.error('એક્સપોર્ટ કરવામાં ભૂલ આવી (Export failed)');
        });
      });
    } catch (e) {
      console.error(e);
      toast.error('એક્સપોર્ટ કરવામાં ભૂલ આવી (Export failed)');
    }
  };

  const gcQuery = gcSearch.trim().toLowerCase();
  const gcFiltered = (greencards || []).filter(c => {
    const exp = new Date(c.valid_until) < new Date();
    if (gcStatusFilter === 'active' && exp) return false;
    if (gcStatusFilter === 'expired' && !exp) return false;
    if (gcCourierFilter !== 'all' && (c.courier_status || 'Not Dispatched') !== gcCourierFilter) return false;
    return !gcQuery || [c.full_name, c.user_phone, c.card_number].some(v => String(v || '').toLowerCase().includes(gcQuery));
  });

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans">
        {/* Mobile top bar with hamburger */}
        <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-[#172554] text-white flex items-center justify-between px-4 z-40 shadow-lg">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="p-2 -ml-2 rounded-lg hover:bg-white/10"
              aria-label="Open menu"
              data-testid="admin-hamburger-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            <span className="font-bold text-base tracking-wide truncate">Kagajbook</span>
          </div>
          <div className="flex items-center gap-2 text-xs font-medium">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Online
          </div>
        </div>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileSidebarOpen(false)}
          data-testid="admin-sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <div
        className={`bg-[#172554] text-white flex flex-col w-64 shadow-xl fixed md:sticky top-0 md:top-0 h-screen z-50 md:z-10 transition-transform duration-300 md:translate-x-0 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        data-testid="admin-sidebar"
      >
        <div className="p-6 flex items-center gap-3 border-b border-white/10 justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl tracking-wide">Kagajbook</span>
          </div>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="md:hidden text-white/70 hover:text-white p-1"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {menuItems.map(item => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => { setActiveTab(item.id); setMobileSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === item.id ? 'bg-[#F5B800] text-[#172554] shadow-md' : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium text-sm">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2.5 rounded-lg transition-colors font-medium text-sm"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden bg-gray-50 pt-14 md:pt-0">
        <header className="hidden md:flex bg-white shadow-sm px-8 py-4 justify-between items-center z-10">
          <h2 className="text-xl font-bold text-[#172033] flex items-center gap-2">
            {(() => {
              const Item = menuItems.find(m => m.id === activeTab);
              const Icon = Item?.icon;
              return Icon ? <Icon className="w-6 h-6 text-[#172554]" /> : null;
            })()}
            {menuItems.find(m => m.id === activeTab)?.label}
          </h2>
          <div className="flex items-center gap-3 text-sm text-[#64748B] font-medium">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Admin Online
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          
          {activeTab === 'dashboard' && (() => {
            const NAVY = '#172554';
            const YELLOW = '#F5B800';
            const d = insightsData || { total_visitors: 0, mobile_visitors: 0, desktop_visitors: 0, daily_series: [] };
            const pieData = [
              { name: 'Mobile (મોબાઈલ)', value: d.mobile_visitors || 0 },
              { name: 'Desktop (ડેસ્કટોપ)', value: d.desktop_visitors || 0 },
            ];
            const PIE_COLORS = [YELLOW, NAVY];
            const shortDate = (s) => {
              try { const p = s.split('-'); return `${p[2]}/${p[1]}`; } catch { return s; }
            };
            const byStatus = appStats.by_status || {};
            const pendingCount = byStatus.Pending || 0;
            const inprocessCount = byStatus.Inprocess || 0;
            const completeCount = byStatus.Complete || 0;
            const submittedCount = byStatus.Submitted || 0;
            // Drafts are intentionally excluded from the top-level "Pending" badge sum
            // so admins aren't distracted by unfinished/unpaid checkouts.

            // Icon tile config: badge value + label + Navy/Yellow color scheme
            const tiles = [
              { id: 'categories',    icon: FolderTree,    label: 'Main Categories',     glabel: 'મુખ્ય કેટેગરી',   badge: (categories || []).length,        badgeLabel: 'Active' },
              { id: 'subcategories', icon: ListTree,      label: 'Sub Categories',      glabel: 'સબ કેટેગરી',      badge: (subCategories || []).length,     badgeLabel: 'Active' },
              { id: 'form_master',   icon: Settings,      label: 'Form Master',         glabel: 'ફોર્મ માસ્ટર',    badge: null,                              badgeLabel: '' },
              { id: 'cities',        icon: FolderTree,    label: 'City Management',     glabel: 'શહેર વ્યવસ્થાપન', badge: (cities || []).length,            badgeLabel: 'Cities' },
              { id: 'announcements', icon: Megaphone,     label: 'Announcements',       glabel: 'ઘોષણાઓ',         badge: (announcements || []).length,     badgeLabel: 'Live' },
              { id: 'links',         icon: LinkIcon,      label: 'Important Links',     glabel: 'મહત્વપૂર્ણ લિંક્સ', badge: (links || []).length,             badgeLabel: 'Links' },
              { id: 'users',         icon: Users,         label: 'User Management',     glabel: 'યુઝર વ્યવસ્થાપન', badge: (users || []).length,             badgeLabel: 'Users' },
              { id: 'bookings',      icon: CalendarCheck, label: 'Booking Management',  glabel: 'બુકિંગ વ્યવસ્થાપન', badge: pendingCount + submittedCount + inprocessCount, badgeLabel: 'Pending', accent: true },
              { id: 'executives',    icon: UserCog,       label: 'Manage Executives',   glabel: 'એક્ઝિક્યુટિવ્સ',   badge: (executives || []).length,        badgeLabel: 'Team' },
              { id: 'accounts',      icon: BookOpen,      label: 'Account Book',        glabel: 'એકાઉન્ટ બુક',    badge: (accounts || []).length,          badgeLabel: 'Entries' },
              { id: 'blogs',         icon: FileText,      label: 'Manage Blogs',        glabel: 'બ્લોગ મેનેજર',    badge: null,                              badgeLabel: '' },
              { id: 'bulk_emails',   icon: Mail,          label: 'Bulk Email Manager',  glabel: 'બલ્ક ઈમેલ',       badge: null,                              badgeLabel: '' },
              { id: 'insights',      icon: TrendingUp,    label: 'Visitor Insights',    glabel: 'મુલાકાતી અંતર્દૃષ્ટિ', badge: visitorsToday,                    badgeLabel: 'Today' },
              { id: 'settings',      icon: Settings,      label: 'Site Settings',       glabel: 'સાઇટ સેટિંગ્સ',   badge: null,                              badgeLabel: '' },
            ];

            return (
              <div className="space-y-8" data-testid="admin-dashboard-home">
                {/* Top Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center items-center text-center">
                    <CalendarCheck className="w-10 h-10 text-blue-500 mb-2" />
                    <h3 className="text-3xl font-bold text-[#172033]" data-testid="dashboard-total-bookings">{(appStats.Total || 0).toLocaleString('en-IN')}</h3>
                    <p className="text-[#64748B] text-sm font-medium">Total Bookings</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center items-center text-center">
                    <Users className="w-10 h-10 text-green-500 mb-2" />
                    <h3 className="text-3xl font-bold text-[#172033]">{(users || []).length}</h3>
                    <p className="text-[#64748B] text-sm font-medium">Registered Users</p>
                  </div>
                  <div className="bg-[#172554] p-6 rounded-xl shadow-sm border border-[#F5B800]/30 flex flex-col justify-center items-center text-center relative overflow-hidden" data-testid="visitors-today-tile">
                    <div className="absolute top-2 right-2 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                      <span className="text-[9px] uppercase tracking-wider text-green-400 font-bold">LIVE</span>
                    </div>
                    <TrendingUp className="w-10 h-10 text-[#F5B800] mb-2" />
                    <h3 className="text-3xl font-bold text-white">
                      {visitorsToday === null ? '—' : visitorsToday.toLocaleString('en-IN')}
                    </h3>
                    <p className="text-[#F5B800] text-sm font-bold">Visitors Today</p>
                    <p className="text-[10px] text-white/60 text-gujarati">આજના મુલાકાતીઓ</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center items-center text-center">
                    <FolderTree className="w-10 h-10 text-purple-500 mb-2" />
                    <h3 className="text-3xl font-bold text-[#172033]">{(categories || []).length}</h3>
                    <p className="text-[#64748B] text-sm font-medium">Main Categories</p>
                  </div>
                  <GreenCardSummary cards={greencards || []} onOpen={() => setActiveTab('greencards')} />
                </div>

                {/* Quick Access Icon Grid */}
                <div data-testid="admin-quick-access-grid">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-[#172554] flex items-center gap-2">
                        <LayoutDashboard className="w-5 h-5 text-[#F5B800]" />
                        Quick Access (ઝડપી પ્રવેશ)
                      </h3>
                      <p className="text-xs text-[#64748B] mt-0.5">All admin modules — tap any card to open</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {tiles.map(t => {
                      const Icon = t.icon;
                      const showBadge = t.badge !== null && t.badge !== undefined;
                      return (
                        <button
                          key={t.id}
                          onClick={() => setActiveTab(t.id)}
                          data-testid={`quick-access-${t.id}`}
                          className={`group relative overflow-hidden text-left rounded-xl p-4 border transition-all hover:-translate-y-0.5 hover:shadow-lg flex flex-col justify-between ${t.accent ? 'bg-[#172554] border-[#F5B800]/40 hover:border-[#F5B800] min-h-[140px]' : 'bg-white border-gray-200 hover:border-[#F5B800] min-h-[140px]'}`}
                        >
                          {/* Yellow corner accent */}
                          <span className="absolute -top-6 -right-6 w-16 h-16 rounded-full bg-[#F5B800]/10 group-hover:bg-[#F5B800]/25 transition-colors"></span>

                          <div className="flex items-start justify-between relative w-full">
                            <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${t.accent ? 'bg-[#F5B800] text-[#172554]' : 'bg-[#172554] text-[#F5B800] group-hover:bg-[#F5B800] group-hover:text-[#172554]'} transition-colors`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            {showBadge && (
                              <div className="flex flex-col items-end shrink-0 pl-2">
                                <span className={`text-[9px] sm:text-[10px] uppercase tracking-wider font-bold ${t.accent ? 'text-[#F5B800]' : 'text-[#64748B]'}`}>{t.badgeLabel}</span>
                                <span className={`text-lg sm:text-xl font-extrabold leading-none ${t.accent ? 'text-white' : 'text-[#172554]'}`} data-testid={`quick-access-badge-${t.id}`}>
                                  {t.badge === null || t.badge === undefined ? '—' : Number(t.badge).toLocaleString('en-IN')}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="mt-auto pt-3 relative w-full">
                            <div className={`font-bold text-sm truncate ${t.accent ? 'text-white' : 'text-[#172554]'}`}>{t.label}</div>
                            <div className={`text-[11px] text-gujarati truncate ${t.accent ? 'text-[#F5B800]/80' : 'text-[#64748B]'}`}>{t.glabel}</div>
                          </div>

                          <div className={`mt-2 flex items-center gap-1 text-[10px] font-bold ${t.accent ? 'text-[#F5B800]' : 'text-[#172554] group-hover:text-[#F5B800]'} relative w-full`}>
                            Open <ChevronRight className="w-3 h-3" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Recent Activity Feed */}
                <div data-testid="admin-recent-activity">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-[#172554] flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#F5B800]" />
                        Recent Activity (તાજેતરની પ્રવૃત્તિ)
                      </h3>
                      <p className="text-xs text-[#64748B] mt-0.5">Last 10 events — new bookings, payments &amp; status changes</p>
                    </div>
                    <span className="flex items-center gap-1 text-[10px] font-bold text-[#64748B] uppercase tracking-wider">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                      Live · refreshes 30s
                    </span>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {(recentActivity || []).length === 0 ? (
                      <div className="p-8 text-center text-[#64748B] text-sm text-gujarati">
                        હજુ કોઈ પ્રવૃત્તિ નથી
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {recentActivity.slice(0, 10).map((ev, idx) => {
                          const cfg = ev.type === 'booking_created'
                            ? { Icon: CalendarCheck, ring: 'bg-blue-100 text-blue-600', tag: 'New Booking', gtag: 'નવી બુકિંગ', tone: 'text-blue-700' }
                            : ev.type === 'payment_received'
                            ? { Icon: CreditCard, ring: 'bg-green-100 text-green-700', tag: 'Payment Received', gtag: 'ચુકવણી પ્રાપ્ત', tone: 'text-green-700' }
                            : { Icon: TrendingUp, ring: 'bg-[#F5B800]/20 text-[#172554]', tag: `Status: ${ev.status || ''}`, gtag: 'સ્થિતિ બદલાઈ', tone: 'text-[#172554]' };
                          const Icon = cfg.Icon;
                          const when = (() => {
                            try {
                              const d = new Date(ev.at);
                              const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
                              if (diff < 60) return `${diff}s ago`;
                              if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                              if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                              return `${Math.floor(diff / 86400)}d ago`;
                            } catch { return ''; }
                          })();
                          return (
                            <li
                              key={idx}
                              className="p-3 md:p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors cursor-pointer"
                              onClick={() => setActiveTab('bookings')}
                              data-testid={`activity-item-${idx}`}
                            >
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.ring}`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`text-[10px] font-extrabold uppercase tracking-wider ${cfg.tone}`}>{cfg.tag}</span>
                                  <span className="text-[10px] text-[#64748B] text-gujarati">{cfg.gtag}</span>
                                </div>
                                <div className="text-sm font-bold text-[#172554] truncate text-gujarati">
                                  {ev.actor || '—'} <span className="text-[#64748B] font-medium">·</span>{' '}
                                  <span className="text-[#64748B] font-medium">{ev.sub_category || ''}</span>
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="text-[11px] font-mono text-[#172554] font-bold">{ev.application_number}</div>
                                <div className="text-[10px] text-[#64748B] font-medium">{when}</div>
                                {ev.type !== 'status_changed' && Number(ev.amount) > 0 && (
                                  <div className="text-[11px] font-bold text-green-700">₹{Number(ev.amount).toLocaleString('en-IN')}</div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Insights Preview (Charts) */}
                <div data-testid="admin-dashboard-insights">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-[#172554] flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#F5B800]" />
                        Visitor Insights (મુલાકાતી અંતર્દૃષ્ટિ)
                      </h3>
                      <p className="text-xs text-[#64748B] mt-0.5">Last 7 days overview — open Insights for full analytics</p>
                    </div>
                    <button
                      onClick={() => setActiveTab('insights')}
                      className="text-xs font-bold text-[#172554] hover:text-[#F5B800] flex items-center gap-1"
                      data-testid="dashboard-view-full-insights"
                    >
                      View Full Insights <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className="bg-[#172554] px-5 py-3 flex justify-between items-center">
                        <h4 className="text-white font-bold tracking-wide">Daily Visitors (દૈનિક મુલાકાતીઓ)</h4>
                        <span className="text-[#F5B800] text-xs font-bold uppercase">Last 7 Days</span>
                      </div>
                      <div className="p-4 h-[300px]">
                        {insightsLoading ? (
                          <div className="h-full flex items-center justify-center text-[#64748B] text-sm">Loading…</div>
                        ) : (d.daily_series || []).length === 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-[#64748B] text-sm gap-2">
                            <TrendingUp className="w-10 h-10 opacity-30" />
                            <p>આ સમયગાળામાં કોઈ મુલાકાત નોંધાયેલ નથી</p>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={(d.daily_series || []).map(r => ({ ...r, dateShort: shortDate(r.date) }))} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                              <XAxis dataKey="dateShort" stroke="#64748B" fontSize={11} interval="preserveStartEnd" />
                              <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                              <Tooltip
                                contentStyle={{ backgroundColor: NAVY, border: 'none', borderRadius: 6, color: '#fff', fontSize: 12 }}
                                itemStyle={{ color: '#fff' }}
                                labelStyle={{ color: YELLOW, fontWeight: 700 }}
                                cursor={{ fill: 'rgba(245,184,0,0.08)' }}
                              />
                              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                              <Bar dataKey="mobile" stackId="a" fill={YELLOW} name="Mobile" />
                              <Bar dataKey="desktop" stackId="a" fill={NAVY} name="Desktop" radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className="bg-[#172554] px-5 py-3">
                        <h4 className="text-white font-bold tracking-wide">Device Split (ડિવાઈસ પ્રમાણ)</h4>
                      </div>
                      <div className="p-4 h-[300px]">
                        {(d.total_visitors || 0) === 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-[#64748B] text-sm gap-2">
                            <Smartphone className="w-10 h-10 opacity-30" />
                            <p>ડેટા ઉપલબ્ધ નથી</p>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} label={({ percent }) => `${Math.round(percent * 100)}%`} labelLine={false} stroke="#fff" strokeWidth={3}>
                                {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                              </Pie>
                              <Tooltip contentStyle={{ backgroundColor: NAVY, border: 'none', borderRadius: 6, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} />
                              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {activeTab === 'categories' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold mb-4 text-[#172554]">Add New Main Category</h3>
              <div className="flex gap-4 mb-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <input type="text" placeholder="Title (e.g. જી.એસ.ટી ફાઇલિંગ સહાય)" className="border border-[#E2E8F0] p-2.5 flex-1 rounded-md outline-none focus:border-blue-500" value={catForm.title} onChange={e => setCatForm({...catForm, title: e.target.value})} />
                <input type="text" placeholder="Slug (optional)" className="border border-[#E2E8F0] p-2.5 flex-1 rounded-md outline-none focus:border-blue-500" value={catForm.slug || ''} onChange={e => setCatForm({...catForm, slug: e.target.value})} />
                <input type="text" placeholder="Icon Type" className="border border-[#E2E8F0] p-2.5 rounded-md w-32 outline-none focus:border-blue-500" value={catForm.icon_type} onChange={e => setCatForm({...catForm, icon_type: e.target.value})} />
                <input type="number" min="0" max="100" placeholder="Discount %" data-testid="cat-discount-input" className="border border-[#E2E8F0] p-2.5 rounded-md w-24 outline-none focus:border-blue-500" value={catForm.discount_percentage || ''} onChange={e => setCatForm({...catForm, discount_percentage: Math.max(0, Math.min(100, parseInt(e.target.value) || 0))})} />
                <button className="bg-[#F5B800] text-[#172554] px-6 py-2.5 rounded-md font-medium hover:bg-[#FFC928] transition-colors shadow-sm" onClick={() => handleCreate('services', catForm)}>Add Category</button>
              </div>

              <h3 className="text-lg font-bold mb-4 text-[#172554] border-b pb-2">Existing 12 Main Categories</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(categories || []).map(c => (
                  <div key={c.id} className="border border-gray-200 p-4 rounded-lg flex flex-col gap-2 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-center w-full">
                      <div>
                        <span className="font-bold text-[#172033] block text-gujarati">{c.title}</span>
                        <span className="text-xs text-[#64748B] block">Slug: {c.slug}</span>
                        <span className="text-xs text-[#64748B]">Icon: {c.icon_type}</span>
                        {c.discount_percentage > 0 && (
                          <span data-testid={`cat-discount-badge-${c.id}`} className="ml-2 text-[10px] font-bold bg-green-600 text-white px-1.5 py-0.5 rounded-full">{c.discount_percentage}% છૂટ</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="text-blue-500 p-2 hover:bg-blue-50 rounded-md transition-colors" onClick={() => setEditingCategory(c)}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="text-red-500 p-2 hover:bg-red-50 rounded-md transition-colors" onClick={() => handleDelete('services', c.id)}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
                      <span className="text-xs font-bold text-gray-500">Order:</span>
                      <input 
                        type="number" 
                        className="border border-[#E2E8F0] rounded p-1 w-16 text-center text-sm" 
                        defaultValue={c.order_num || 0}
                        onBlur={(e) => handleUpdateMainOrder(c.id, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'subcategories' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold mb-4 text-[#172554]">Add New Sub Category</h3>
              <div className="flex flex-col gap-4 mb-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex flex-wrap gap-4">
                  <select className="border border-[#E2E8F0] p-2.5 flex-1 rounded-md outline-none focus:border-blue-500 text-gujarati" value={subCatForm.category_name} onChange={e => setSubCatForm({...subCatForm, category_name: e.target.value})}>
                    {(categories || []).map(c => <option key={c.id} value={c.title}>{c.title}</option>)}
                  </select>
                  <input type="text" placeholder="Sub Title (e.g. પાનકાર્ડ)" className="border border-[#E2E8F0] p-2.5 flex-1 rounded-md outline-none focus:border-blue-500 text-gujarati" value={subCatForm.title} onChange={e => setSubCatForm({...subCatForm, title: e.target.value})} />
                  <input type="text" placeholder="Slug (optional)" className="border border-[#E2E8F0] p-2.5 flex-1 rounded-md outline-none focus:border-blue-500" value={subCatForm.slug || ''} onChange={e => setSubCatForm({...subCatForm, slug: e.target.value})} />
                  <input type="text" placeholder="Lucide Icon" className="border border-[#E2E8F0] p-2.5 rounded-md w-32 outline-none focus:border-blue-500" value={subCatForm.icon_type} onChange={e => setSubCatForm({...subCatForm, icon_type: e.target.value})} />
                  <input type="number" placeholder="Order (e.g. 1)" className="border border-[#E2E8F0] p-2.5 rounded-md w-24 outline-none focus:border-blue-500" value={subCatForm.order_num || ''} onChange={e => setSubCatForm({...subCatForm, order_num: parseInt(e.target.value) || 0})} />
                </div>
                
                <div className="flex flex-wrap gap-4 items-center bg-white p-3 border border-gray-200 rounded">
                  <div className="flex flex-col gap-1 flex-1 min-w-[150px]">
                    <label className="text-xs font-bold text-[#64748B]">અંદાજીત સમય (Est. Days)</label>
                    <input type="text" placeholder="e.g. 3-5 દિવસ" className="border border-[#E2E8F0] p-2.5 rounded-md outline-none focus:border-blue-500 text-gujarati text-sm" value={subCatForm.estimated_days} onChange={e => setSubCatForm({...subCatForm, estimated_days: e.target.value})} />
                  </div>
                  
                  <div className="flex flex-col gap-1 border-l pl-4 flex-1 min-w-[150px]">
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-sm text-[#172554]">
                      <input type="checkbox" checked={subCatForm.normal_enabled !== false} onChange={e => setSubCatForm({...subCatForm, normal_enabled: e.target.checked})} className="w-4 h-4 text-[#F5B800]" />
                      સામાન્ય અરજી
                    </label>
                    <input type="number" placeholder="Fee (e.g. 50)" className="border border-[#E2E8F0] p-2.5 rounded-md w-full outline-none focus:border-blue-500 mt-1" value={subCatForm.fee_amount} onChange={e => setSubCatForm({...subCatForm, fee_amount: parseInt(e.target.value) || 0})} disabled={subCatForm.normal_enabled === false} />
                  </div>

                  <div className="flex flex-col gap-1 border-l pl-4 flex-1 min-w-[150px]">
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-sm text-[#172554]">
                      <input type="checkbox" checked={subCatForm.tatkal_enabled || false} onChange={e => setSubCatForm({...subCatForm, tatkal_enabled: e.target.checked})} className="w-4 h-4 text-[#F5B800]" />
                      તત્કાલ અરજી
                    </label>
                    <input type="number" placeholder="Fee (e.g. 150)" className="border border-[#E2E8F0] p-2.5 rounded-md w-full outline-none focus:border-blue-500 mt-1" value={subCatForm.tatkal_fee_amount} onChange={e => setSubCatForm({...subCatForm, tatkal_fee_amount: parseInt(e.target.value) || 0})} disabled={!subCatForm.tatkal_enabled} />
                  </div>
                </div>

                <div className="bg-yellow-50/60 border border-yellow-200 rounded p-3">
                  <label className="block text-xs font-bold text-[#172554] mb-1.5 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F5B800]"></span>
                    મહત્વની નોંધ (Important Note) — Optional
                  </label>
                  <textarea
                    rows={3}
                    placeholder="દા.ત. અરજી સાથે મૂળ દસ્તાવેજ સ્કેન કરીને અપલોડ કરો. ૨૪ કલાકમાં પ્રક્રિયા શરુ થશે."
                    className="w-full border border-yellow-300 rounded-md p-2.5 text-sm outline-none focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] bg-white text-gujarati resize-y"
                    value={subCatForm.important_note || ''}
                    onChange={e => setSubCatForm({...subCatForm, important_note: e.target.value})}
                    data-testid="subcat-important-note-input"
                  />
                  <p className="text-[10px] text-[#64748B] mt-1">User ને Preview પર highlighted yellow box માં દેખાશે.</p>
                </div>
                
                <div className="flex justify-end pt-2">
                  <button className="bg-[#F5B800] text-[#172554] px-8 py-2.5 rounded-md font-bold hover:bg-[#FFC928] transition-colors shadow-sm" onClick={() => handleCreate('subservices', subCatForm)}>Add Sub Category</button>
                </div>
              </div>

              <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h3 className="text-lg font-bold text-[#172554]">Existing Sub Categories</h3>
                
                <div className="relative group z-20">
                  <button className="bg-white border border-[#E2E8F0] text-[#172554] hover:bg-gray-50 font-bold py-1.5 px-4 rounded-lg flex items-center gap-2 text-sm text-gujarati shadow-sm transition-colors">
                    <Download className="w-4 h-4" /> નિકાસ કરો (Export) <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none group-hover:pointer-events-auto">
                    <button 
                      className="w-full text-left px-4 py-2 text-sm font-bold text-[#172554] hover:bg-gray-50 flex items-center gap-2 border-b text-gujarati"
                      onClick={() => handleExportExcel('SubServices', (subCategories || []).map(s => ({
                        'Sr. No': s.order_num || 0,
                        'Service Name (Gujarati)': s.title,
                        'Category': s.category_name,
                        'Application Fee': s.fee_amount || 0,
                        'Status': 'Active'
                      })))}
                    >
                      <FileSpreadsheet className="w-4 h-4 text-green-600" /> Excel માં નિકાસ
                    </button>
                    <button 
                      className="w-full text-left px-4 py-2 text-sm font-bold text-[#172554] hover:bg-gray-50 flex items-center gap-2 text-gujarati"
                      onClick={() => {
                        handleExportPDF('SubServices', (subCategories || []), [
                          {key: 'order_num', label: 'Sr. No.'},
                          {key: 'title', label: 'Service Name'},
                          {key: 'category_name', label: 'Category'},
                          {key: 'fee_amount', label: 'Fee (Rs)'},
                          {key: 'is_featured', label: 'Important'}
                        ]);
                      }}
                    >
                      <FileText className="w-4 h-4 text-red-600" /> PDF માં નિકાસ
                    </button>
                  </div>
                </div>
              </div>
              
              <ul className="divide-y divide-gray-100 max-h-[700px] overflow-y-auto pr-2">
                {(subCategories || []).map(c => (
                  <li key={c.id} className="py-4 flex flex-col hover:bg-gray-50 px-2 rounded transition-colors">
                    <div className="flex justify-between items-center w-full">
                      <div className="flex flex-col">
                        <span className="font-bold text-[#172033] text-gujarati">{c.title}</span>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="text-xs font-medium text-[#F5B800] bg-blue-50 px-2 py-0.5 rounded w-fit">{c.category_name}</span>
                          {c.estimated_days && (
                            <span className="text-[10px] font-medium text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded text-gujarati" title="Normal SLA">
                              ⏱ {c.estimated_days}
                            </span>
                          )}
                          {c.slug && (
                            <span className="text-[10px] font-medium text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded" title="Slug">
                              /{c.slug}
                            </span>
                          )}
                          {c.tatkal_enabled && (
                            <span className="text-[10px] font-bold text-orange-800 bg-orange-100 border border-orange-200 px-2 py-0.5 rounded" title="Tatkal Fee">
                              ⚡ Tatkal ₹{c.tatkal_fee_amount ?? 0}
                            </span>
                          )}
                          {typeof c.fee_amount === 'number' && (
                            <span className="text-[10px] font-bold text-green-800 bg-green-100 border border-green-200 px-2 py-0.5 rounded">
                              ₹{c.fee_amount}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <button 
                          className="text-xs font-medium bg-blue-100 text-blue-800 px-3 py-1.5 rounded hover:bg-blue-200 transition-colors"
                          onClick={() => setExpandedSubCat(expandedSubCat === c.id ? null : c.id)}
                        >
                          {expandedSubCat === c.id ? 'Close Config' : 'Configure Form'}
                        </button>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-500">Order:</span>
                          <input 
                            type="number" 
                            className="border border-[#E2E8F0] rounded p-1 w-16 text-center text-sm" 
                            defaultValue={c.order_num || 0}
                            onBlur={(e) => handleUpdateOrder(c.id, e.target.value)}
                          />
                        </div>
                        <button 
                          onClick={() => handleToggleFeature(c.id, c.is_featured)}
                          className={`text-xl ${c.is_featured ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
                          title={c.is_featured ? "Remove from Important Services" : "Add to Important Services"}
                        >
                          ★
                        </button>
                        <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-1 rounded border border-gray-200">{c.icon_type}</span>
                        <button className="text-blue-500 p-1 hover:bg-blue-50 rounded transition-colors" onClick={() => setEditingSubCategory(c)}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="text-red-500 p-1 hover:bg-red-50 rounded transition-colors" onClick={() => handleDelete('subservices', c.id)}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {expandedSubCat === c.id && (
                      <SubCategoryFormEditor 
                        subService={c} 
                        API_URL={API_URL} 
                        onUpdate={() => {
                          fetchData();
                        }} 
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'announcements' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold mb-4 text-[#172554]">Add New Announcement (અપડેટ્સ)</h3>
              <div className="flex gap-4 mb-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <input type="text" placeholder="Announcement Text" className="border border-[#E2E8F0] p-2.5 flex-1 rounded-md outline-none focus:border-blue-500 text-gujarati" value={annForm.text} onChange={e => setAnnForm({...annForm, text: e.target.value})} />
                <button className="bg-[#F5B800] text-[#172554] px-6 py-2.5 rounded-md font-medium hover:bg-[#FFC928] transition-colors shadow-sm" onClick={() => handleCreate('announcements', annForm)}>Publish Now</button>
              </div>

              <h3 className="text-lg font-bold mb-4 text-[#172554] border-b pb-2">Announcement History</h3>
              <ul className="divide-y divide-gray-100">
                {(announcements || []).map(c => (
                  <li key={c.id} className="py-4 flex justify-between items-center hover:bg-gray-50 px-2 rounded transition-colors">
                    <div className="flex items-center gap-3">
                      <Megaphone className="w-5 h-5 text-gray-400" />
                      <span className="font-medium text-[#172033] text-gujarati text-sm">{c.text}</span>
                      {c.is_active && <span className="bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded text-xs font-bold shadow-sm">Active</span>}
                    </div>
                    <button className="text-red-500 text-sm font-medium hover:underline" onClick={() => handleDelete('announcements', c.id)}>Delete</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'links' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold mb-4 text-[#172554]">Add New Important Link</h3>
              <div className="flex gap-4 mb-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <input type="text" placeholder="Link Text" className="border border-[#E2E8F0] p-2.5 flex-1 rounded-md outline-none focus:border-blue-500 text-gujarati" value={linkForm.text} onChange={e => setLinkForm({...linkForm, text: e.target.value})} />
                <input type="text" placeholder="URL (e.g. https://...)" className="border border-[#E2E8F0] p-2.5 rounded-md w-64 outline-none focus:border-blue-500" value={linkForm.url} onChange={e => setLinkForm({...linkForm, url: e.target.value})} />
                <button className="bg-[#F5B800] text-[#172554] px-6 py-2.5 rounded-md font-medium hover:bg-[#FFC928] transition-colors shadow-sm" onClick={() => handleCreate('links', linkForm)}>Save Link</button>
              </div>

              <h3 className="text-lg font-bold mb-4 text-[#172554] border-b pb-2">Active Links</h3>
              <ul className="divide-y divide-gray-100">
                {(links || []).map(c => (
                  <li key={c.id} className="py-4 flex justify-between items-center hover:bg-gray-50 px-2 rounded transition-colors">
                    <div className="flex items-center gap-3">
                      <LinkIcon className="w-5 h-5 text-gray-400" />
                      <span className="font-medium text-[#172033] text-gujarati text-sm">{c.text}</span>
                      <a href={c.url} target="_blank" rel="noreferrer" className="text-blue-500 text-xs hover:underline font-mono bg-blue-50 px-2 py-1 rounded">URL</a>
                    </div>
                    <button className="text-red-500 text-sm font-medium hover:underline" onClick={() => handleDelete('links', c.id)}>Delete</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'bookings' && (() => {
            // Unified filter pipeline used by table, Excel export, and PDF export
            const filteredApps = applications || [];
            const clearFilters = () => {
              setBookingTypeFilter('all');
              setBookingStatusFilter('all');
              setBookingDateFrom('');
              setBookingDateTo('');
              setBookingPhoneFilter('');
              setBookingSearch('');
              setDebouncedBookingSearch('');
            };
            const hasActiveFilter = bookingTypeFilter !== 'all' || bookingStatusFilter !== 'all' || !!bookingDateFrom || !!bookingDateTo || !!bookingPhoneFilter || !!debouncedBookingSearch;

            return (
            <div className="space-y-4">
              {/* Status Summary Cards */}
              {(() => {
                const byStatus = appStats.by_status || {};
                const drafts = appStats.Drafts || 0;
                const total = appStats.Total || 0;
                const pending = byStatus.Pending || 0;
                const inprocess = byStatus.Inprocess || 0;
                const complete = byStatus.Complete || 0;
                
                const cards = [
                  { id: 'all',       label: 'Total',      glabel: 'કુલ',           count: total,     tone: 'navy'   },
                  { id: 'Pending',   label: 'Pending',    glabel: 'પેન્ડિંગ',       count: pending,   tone: 'yellow' },
                  { id: 'Inprocess', label: 'In Process', glabel: 'પ્રક્રિયા ચાલુ છે', count: inprocess, tone: 'blue'   },
                  { id: 'Complete',  label: 'Completed',  glabel: 'પૂર્ણ',           count: complete,  tone: 'green'  },
                  { id: 'Draft',     label: 'Drafts',     glabel: 'કાચું ફોર્મ',     count: drafts,    tone: 'gray'   },
                ];
                const toneClasses = {
                  navy:   { card: 'bg-[#172554] border-[#F5B800]/40',           title: 'text-[#F5B800]', count: 'text-white',       glabel: 'text-white/70',     icon: 'bg-[#F5B800] text-[#172554]' },
                  yellow: { card: 'bg-white border-[#F5B800]',                  title: 'text-[#a16207]', count: 'text-[#a16207]',   glabel: 'text-[#64748B]',    icon: 'bg-[#F5B800]/20 text-[#a16207]' },
                  blue:   { card: 'bg-white border-blue-400',                   title: 'text-blue-700',  count: 'text-blue-700',    glabel: 'text-[#64748B]',    icon: 'bg-blue-100 text-blue-700' },
                  green:  { card: 'bg-white border-green-500',                  title: 'text-green-700', count: 'text-green-700',   glabel: 'text-[#64748B]',    icon: 'bg-green-100 text-green-700' },
                  gray:   { card: 'bg-gray-50 border-gray-300',                 title: 'text-gray-600',  count: 'text-gray-700',    glabel: 'text-gray-500',     icon: 'bg-gray-200 text-gray-600' },
                };
                return (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-testid="booking-status-summary">
                    {cards.map(c => {
                      const t = toneClasses[c.tone];
                      const active = bookingStatusFilter === c.id;
                      return (
                        <button
                          key={c.id}
                          onClick={() => setBookingStatusFilter(c.id)}
                          data-testid={`booking-summary-${c.id.toLowerCase()}`}
                          className={`text-left rounded-xl border-2 p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${t.card} ${active ? 'ring-2 ring-[#F5B800] ring-offset-1' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className={`text-[10px] uppercase tracking-wider font-extrabold ${t.title}`}>{c.label}</div>
                              <div className={`text-[11px] text-gujarati ${t.glabel}`}>{c.glabel}</div>
                            </div>
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${t.icon}`}>
                              <CalendarCheck className="w-4 h-4" />
                            </div>
                          </div>
                          <div className={`mt-3 text-3xl font-extrabold leading-none ${t.count}`} data-testid={`booking-summary-count-${c.id.toLowerCase()}`}>
                            {Number(c.count).toLocaleString('en-IN')}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-4 border-b pb-2 flex-wrap gap-3">
                <div>
                  <h3 className="text-lg font-bold text-[#172554]">All Applications / Bookings</h3>
                  <div className="text-xs text-[#64748B] mt-0.5">
                    Showing <b className="text-[#172554]">{filteredApps.length}</b> of <span data-testid="booking-total-count">{appTotalCount.toLocaleString('en-IN')}</span> applications
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    data-testid="booking-export-excel"
                    onClick={async () => handleExportExcel('Bookings', (await fetchAllFilteredApps()).map(a => ({
                      'Application No': a.application_number,
                      'User Name': a.full_name,
                      'User Phone': a.user_phone,
                      'City': a.city || '',
                      'Category': a.category_name,
                      'Service': a.sub_category_name,
                      'Type': a.application_type || 'સામાન્ય અરજી (Normal)',
                      'Date': new Date(a.created_at).toLocaleDateString(),
                      'Status': a.status,
                      'Payment Status': a.payment_status || 'Pending',
                      'Amount': a.amount || 0,
                      'Form Fields': JSON.stringify(a.form_data || {})
                    })))} 
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors"
                  >
                    Export Excel
                  </button>
                  <button 
                    data-testid="booking-export-pdf"
                    onClick={async () => handleExportPDF('Bookings', await fetchAllFilteredApps(), [
                      {key: 'application_number', label: 'App No'},
                      {key: 'full_name', label: 'Name'},
                      {key: 'user_phone', label: 'Phone'},
                      {key: 'city', label: 'City'},
                      {key: 'sub_category_name', label: 'Service'},
                      {key: 'application_type', label: 'App Type'},
                      {key: 'status', label: 'Status'}
                    ])} 
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors"
                  >
                    Export PDF
                  </button>
                </div>
              </div>

              {/* Drilled-down filter chip when viewing a single user's bookings */}
              {bookingPhoneFilter && (() => {
                const drilledUser = (users || []).find(u => u.phone === bookingPhoneFilter);
                return (
                  <div
                    className="mb-3 flex items-center gap-2 bg-[#FFF8DB] border border-[#F5B800] rounded-lg px-3 py-2 shadow-sm w-fit"
                    data-testid="booking-phone-filter-chip"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#F5B800] flex items-center justify-center shrink-0">
                      <Users className="w-3.5 h-3.5 text-[#172554]" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold leading-none">Filtered by user</span>
                      <span className="text-sm font-bold text-[#172554] text-gujarati leading-tight">
                        {drilledUser?.name || '—'} <span className="font-mono text-xs text-[#64748B]">· {bookingPhoneFilter}</span>
                      </span>
                    </div>
                    <button
                      onClick={() => setBookingPhoneFilter('')}
                      className="ml-2 p-1 rounded hover:bg-[#F5B800]/30"
                      data-testid="booking-phone-filter-clear"
                      title="Clear user filter"
                    >
                      <X className="w-4 h-4 text-[#172554]" />
                    </button>
                  </div>
                );
              })()}

              {/* Filter Tabs: All / Urgent / Normal */}
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="booking-type-filter">
                {(() => {
                  const urgentCount = appTypeCounts.urgent;
                  const normalCount = appTypeCounts.normal;
                  const draftTotal = bookingStatusFilter === 'Draft' ? appTotalCount : 0;
                  const btnCls = (v) => `px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${bookingTypeFilter === v ? 'bg-[#172554] text-white border-[#172554]' : 'bg-white text-[#172554] border-gray-300 hover:border-[#172554]'}`;
                  const urgentBtnCls = bookingTypeFilter === 'urgent' 
                    ? 'bg-gradient-to-r from-[#F5B800] to-[#FFC928] text-[#172554] border-[#F5B800] shadow-sm ring-1 ring-[#F5B800]/50' 
                    : 'bg-white text-[#172554] border-gray-300 hover:border-[#F5B800]';
                  return (
                    <>
                      <button data-testid="booking-filter-all" onClick={() => setBookingTypeFilter('all')} className={btnCls('all')}>
                        All Applications <span className="ml-1 opacity-70">({appTypeCounts.all})</span>
                      </button>
                      <button 
                        data-testid="booking-filter-urgent"
                        onClick={() => setBookingTypeFilter('urgent')} 
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors inline-flex items-center gap-1.5 ${urgentBtnCls}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z"/></svg>
                        Urgent Only <span className="ml-0.5 opacity-70">({urgentCount})</span>
                      </button>
                      <button data-testid="booking-filter-normal" onClick={() => setBookingTypeFilter('normal')} className={btnCls('normal')}>
                        Normal Only <span className="ml-1 opacity-70">({normalCount})</span>
                      </button>
                      {draftTotal > 0 && (
                        <button
                          data-testid="booking-send-all-drafts"
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to send WhatsApp reminders to ALL ${draftTotal} draft applicants?`)) {
                              const all = await fetchAllFilteredApps({ status: 'Draft' });
                              handleSendDraftReminder(all.map(a => a.id));
                            }
                          }}
                          className="ml-auto px-3 py-1.5 rounded-lg text-xs font-bold bg-green-600 text-white hover:bg-green-700 shadow-sm flex items-center gap-1.5 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                          બધાને મોકલો (Send to All Drafts)
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Date Range + Status Filters */}
              <div className="flex items-end gap-3 mb-4 flex-wrap p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]" data-testid="booking-advanced-filters">
                <div className="flex flex-col flex-1 min-w-[200px]">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wide mb-1">Search</label>
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-[#64748B] absolute left-2 top-1.5" />
                    <input 
                      type="text" 
                      placeholder="એપ્લિકેશન નંબર, નામ, મોબાઈલ..."
                      value={bookingSearch}
                      onChange={(e) => setBookingSearch(e.target.value)}
                      className="border border-[#E2E8F0] rounded pl-7 pr-2 py-1 text-xs font-bold text-[#172554] outline-none focus:border-[#172554] w-full bg-white"
                      data-testid="booking-search-input"
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wide mb-1">Date From</label>
                  <input 
                    type="date" 
                    value={bookingDateFrom}
                    onChange={(e) => setBookingDateFrom(e.target.value)}
                    className="border border-[#E2E8F0] rounded px-2 py-1.5 text-xs font-bold text-[#172554] outline-none focus:border-[#172554]"
                    data-testid="booking-date-from"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wide mb-1">Date To</label>
                  <input 
                    type="date" 
                    value={bookingDateTo}
                    onChange={(e) => setBookingDateTo(e.target.value)}
                    className="border border-[#E2E8F0] rounded px-2 py-1.5 text-xs font-bold text-[#172554] outline-none focus:border-[#172554]"
                    data-testid="booking-date-to"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wide mb-1">Status</label>
                  <select 
                    value={bookingStatusFilter}
                    onChange={(e) => setBookingStatusFilter(e.target.value)}
                    className="border border-[#E2E8F0] rounded px-2 py-1.5 text-xs font-bold text-[#172554] outline-none focus:border-[#172554] bg-white"
                    data-testid="booking-status-filter"
                  >
                    <option value="all">All Real Applications</option>
                    <option value="Submitted">Submitted (સબમિટ)</option>
                    <option value="Pending">પેન્ડિંગ (Pending)</option>
                    <option value="Inprocess">પ્રક્રિયા ચાલુ (In Process)</option>
                    <option value="Approval">અંતિમ મંજૂરી (Approval)</option>
                    <option value="Complete">પૂર્ણ (Complete)</option>
                    <option value="Cancelled">રદ (Cancelled)</option>
                    <option value="Draft">કાચું ફોર્મ (Drafts / Unpaid)</option>
                  </select>
                </div>
                {hasActiveFilter && (
                  <button 
                    onClick={clearFilters}
                    className="text-xs font-bold text-[#172554] border-2 border-[#172554] hover:bg-[#172554] hover:text-white rounded px-3 py-1.5 transition-colors flex items-center gap-1"
                    data-testid="booking-clear-filters"
                  >
                    <X className="w-3 h-3" /> Clear Filters
                  </button>
                )}
                <div className="ml-auto text-xs text-[#64748B] font-bold">
                  Result: <span className="text-[#172554]" data-testid="booking-result-count">{appTotalCount.toLocaleString('en-IN')}</span> applications
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-[#64748B] text-sm uppercase tracking-wider">
                      <th className="p-3 border-b font-semibold">User Details</th>
                      <th className="p-3 border-b font-semibold">City</th>
                      <th className="p-3 border-b font-semibold">Service</th>
                      <th className="p-3 border-b font-semibold">Type</th>
                      <th className="p-3 border-b font-semibold">Fields Summary</th>
                      <th className="p-3 border-b font-semibold">Documents</th>
                      <th className="p-3 border-b font-semibold">Status</th>
                      <th className="p-3 border-b font-semibold">Executive</th>
                      <th className="p-3 border-b font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-[#172033] divide-y divide-gray-100">
                    {filteredApps.map(app => {
                      const requiredCount = Array.isArray(app.required_uploads) ? app.required_uploads.length : 0;
                      const skippedCount = Array.isArray(app.skipped_uploads) ? app.skipped_uploads.length : 0;
                      const uploadedCount = requiredCount - skippedCount;
                      
                      return (
                      <React.Fragment key={app.id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="p-3">
                          <div className="font-bold text-[#172554]">{app.application_number}</div>
                          <div className="font-bold text-gujarati mt-1">{app.full_name}</div>
                          <div className="text-xs text-[#64748B]">{app.user_phone}</div>
                        </td>
                        <td className="p-3 font-bold text-gujarati text-sm text-[#64748B]">
                          {app.city || '-'}
                        </td>
                        <td className="p-3">
                          <div className="font-bold text-gujarati">{app.sub_category_name}</div>
                          <div className="text-xs text-[#64748B] text-gujarati mt-1">{app.category_name}</div>
                          <div className="text-xs text-[#64748B] mt-1">{new Date(app.created_at).toLocaleDateString()}</div>
                        </td>
                        <td className="p-3">
                          {(app.application_type || '').includes('Urgent') || (app.application_type || '').includes('તત્કાલ') ? (
                            <span 
                              data-testid={`admin-urgent-ribbon-${app.application_number}`}
                              className="inline-flex items-center gap-1 bg-gradient-to-r from-[#F5B800] to-[#FFC928] text-[#172554] px-2.5 py-0.5 rounded-full text-[11px] font-extrabold text-gujarati shadow-sm ring-1 ring-[#F5B800]/50 uppercase tracking-wide"
                              title="તત્કાલ અરજી (Urgent)"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z"/></svg>
                              તત્કાલ
                            </span>
                          ) : (
                            <span className="inline-flex items-center bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 rounded-full text-[11px] font-bold text-gujarati">
                              સામાન્ય
                            </span>
                          )}
                          <div data-testid={`admin-amount-${app.application_number}`} className="text-xs mt-1">
                            {app.discount_amount > 0 ? (
                              <>
                                <span className="line-through text-gray-400 mr-1">₹{app.original_fee}</span>
                                <span className="text-green-700 font-bold">-{app.discount_percentage}% (₹{app.discount_amount})</span>
                                <div className="font-bold text-[#172554]">કુલ: ₹{app.amount}</div>
                              </>
                            ) : (
                              <span className="font-bold text-[#172554]">₹{app.amount || 0}</span>
                            )}
                            {app.green_card_discount_applied && (
                              <div className="mt-1">
                                <span className="inline-block px-1.5 py-0.5 rounded bg-green-100 text-green-700 border border-green-200 text-[10px] font-bold shadow-sm whitespace-nowrap text-gujarati" title="Green Card Discount Applied">
                                  ગ્રીન કાર્ડ ડિસ્કાઉન્ટ
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <button
                            onClick={() => setShowFieldsSummary(showFieldsSummary === app.application_number ? null : app.application_number)}
                            className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 px-2 py-1 rounded"
                          >
                            {Object.keys(app.form_data || {}).length} Fields
                          </button>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-bold bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200">
                              {uploadedCount} Uploaded
                            </span>
                            {skippedCount > 0 && (
                              <span className="text-xs font-bold bg-orange-50 text-orange-700 px-2 py-1 rounded border border-orange-200">
                                {skippedCount} Not Provided
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          {(() => {
                            const statusColors = {
                              'Draft': 'bg-gray-100 text-gray-700 border-gray-200',
                              'Submitted': 'bg-blue-100 text-blue-700 border-blue-200',
                              'Pending': 'bg-orange-100 text-orange-700 border-orange-200',
                              'Inprocess': 'bg-yellow-100 text-yellow-700 border-yellow-200',
                              'Approval': 'bg-purple-100 text-purple-700 border-purple-200',
                              'Complete': 'bg-green-100 text-green-700 border-green-200',
                              'Cancelled': 'bg-red-100 text-red-700 border-red-200'
                            };
                            return (
                              <select 
                                value={app.status}
                                onChange={(e) => handleStatusUpdate(app.application_number, e.target.value)}
                                className={`border text-xs font-bold rounded focus:ring-2 focus:outline-none block w-full p-1 transition-colors ${statusColors[app.status] || 'bg-gray-50 border-gray-200 text-[#172554]'}`}
                                data-testid={`app-status-select-${app.application_number}`}
                              >
                                <option value="Draft" disabled={app.status !== 'Draft'} className="bg-gray-100 text-gray-700 font-bold">કાચું ફોર્મ (Drafts / Unpaid)</option>
                                <option value="Submitted" disabled={['Pending', 'Inprocess', 'Approval', 'Complete', 'Cancelled'].includes(app.status)} className="bg-blue-100 text-blue-700 font-bold">સબમિટ કરેલ (Submitted)</option>
                                <option value="Pending" disabled={['Inprocess', 'Approval', 'Complete', 'Cancelled'].includes(app.status)} className="bg-orange-100 text-orange-700 font-bold">પેન્ડિંગ (Pending)</option>
                                <option value="Inprocess" disabled={['Approval', 'Complete', 'Cancelled'].includes(app.status)} className="bg-yellow-100 text-yellow-700 font-bold">પ્રક્રિયા ચાલુ છે (In Process)</option>
                                <option value="Approval" disabled={['Complete', 'Cancelled'].includes(app.status)} className="bg-purple-100 text-purple-700 font-bold">અંતિમ મંજૂરી (Approval)</option>
                                <option value="Complete" disabled={['Cancelled'].includes(app.status)} className="bg-green-100 text-green-700 font-bold">પૂર્ણ (Complete)</option>
                                <option value="Cancelled" disabled={['Complete'].includes(app.status)} className="bg-red-100 text-red-700 font-bold">રદ / રિફંડ (Cancelled / Refund)</option>
                              </select>
                            );
                          })()}
                        </td>
                        <td className="p-3 min-w-[210px] max-w-[280px]">
                          {(() => {
                            const history = app.status_history || [];
                            const latest = [...history].reverse().find(h => h && h.remark && h.remark.trim() !== '');
                            const savedRemark = app.remark || (latest ? latest.remark : '');
                            const draft = remarkDrafts[app.application_number];
                            const value = draft !== undefined ? draft : savedRemark;
                            const isSaving = remarkSavingKey === app.application_number;
                            const isTplOpen = remarkTemplateOpenFor === app.application_number;
                            return (
                              <div className="relative">
                                <div className="flex items-center gap-1">
                                  <div className="relative flex-1">
                                    <input
                                      type="text"
                                      value={value}
                                      placeholder="+ રીમાર્ક ઉમેરો"
                                      disabled={isSaving}
                                      onChange={(e) => setRemarkDrafts(prev => ({ ...prev, [app.application_number]: e.target.value }))}
                                      onBlur={() => saveRemark(app.application_number, value, savedRemark)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                                        if (e.key === 'Escape') {
                                          setRemarkDrafts(prev => {
                                            const next = { ...prev };
                                            delete next[app.application_number];
                                            return next;
                                          });
                                          e.target.blur();
                                        }
                                      }}
                                      data-testid={`app-remark-input-${app.application_number}`}
                                      className="w-full text-xs bg-white border border-gray-200 rounded pl-2 pr-6 py-1 text-[#172554] placeholder:text-gray-400 placeholder:italic focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] outline-none transition-colors"
                                    />
                                    {isSaving && (
                                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-blue-500">સેવ...</span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => setRemarkTemplateOpenFor(isTplOpen ? null : app.application_number)}
                                    className={`shrink-0 w-6 h-6 rounded flex items-center justify-center border transition-colors ${isTplOpen ? 'bg-[#F5B800] border-[#F5B800] text-[#172554]' : 'bg-white border-gray-200 text-[#F5B800] hover:bg-[#FEF9E7] hover:border-[#F5B800]'}`}
                                    title="Quick remarks"
                                    aria-label="Quick remark templates"
                                    data-testid={`app-remark-templates-toggle-${app.application_number}`}
                                  >
                                    <Zap className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                {isTplOpen && (
                                  <>
                                    {/* Click-outside overlay */}
                                    <div
                                      className="fixed inset-0 z-40"
                                      onClick={() => setRemarkTemplateOpenFor(null)}
                                    />
                                    <div
                                      className="absolute right-0 top-full mt-1 z-50 w-[240px] bg-white border border-gray-200 rounded-lg shadow-xl p-2"
                                      data-testid={`app-remark-templates-popover-${app.application_number}`}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-gray-100">
                                        <span className="text-[10px] font-bold text-[#172554] uppercase tracking-wide">ઝડપી રીમાર્ક</span>
                                        <button
                                          onClick={() => setRemarkTemplateOpenFor(null)}
                                          className="text-gray-400 hover:text-gray-700"
                                          aria-label="Close"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                      <div className="flex flex-col gap-1 max-h-[240px] overflow-y-auto">
                                        {remarkTemplates.map((tpl, idx) => (
                                          <button
                                            key={idx}
                                            type="button"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                              setRemarkDrafts(prev => ({ ...prev, [app.application_number]: tpl }));
                                              setRemarkTemplateOpenFor(null);
                                              saveRemark(app.application_number, tpl, savedRemark);
                                            }}
                                            className="text-left text-xs px-2 py-1.5 rounded hover:bg-[#FEF9E7] hover:text-[#172554] text-gray-700 text-gujarati transition-colors"
                                            data-testid={`app-remark-template-option-${app.application_number}-${idx}`}
                                          >
                                            {tpl}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="p-3">
                          {app.assigned_executive?.name ? (
                            <div className="flex flex-col gap-0.5" data-testid={`exec-cell-${app.application_number}`}>
                              <div className="flex items-center gap-1.5">
                                <UserCog className="w-3.5 h-3.5 text-[#172554]" />
                                <span className="font-bold text-[#172554] text-xs text-gujarati">{app.assigned_executive.name}</span>
                              </div>
                              <div className="text-[10px] text-[#64748B] text-gujarati">{app.assigned_executive.designation}</div>
                              <button
                                onClick={() => {
                                  setAssignSelectedExec(app.assigned_executive?.id || '');
                                  setAssignExecModal({ appNumber: app.application_number, currentExecId: app.assigned_executive?.id });
                                }}
                                className="text-[10px] text-blue-700 font-bold hover:underline text-left"
                                data-testid={`reassign-exec-${app.application_number}`}
                              >
                                Reassign
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                if (!Array.isArray(executives) || executives.length === 0) {
                                  toast.error('પ્રથમ Manage Executives ટૅબમાં એક્ઝિક્યુટિવ ઉમેરો');
                                  return;
                                }
                                setAssignSelectedExec(executives[0]?.id || '');
                                setAssignExecModal({ appNumber: app.application_number, currentExecId: null });
                              }}
                              className="text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 px-2 py-1 rounded"
                              data-testid={`assign-exec-${app.application_number}`}
                            >
                              + Assign
                            </button>
                          )}
                        </td>
                        <td className="p-3 flex items-center gap-2">
                          <button
                            onClick={() => setExpandedApp(expandedApp === app.application_number ? null : app.application_number)}
                            className="text-xs font-bold text-[#172554] bg-yellow-100 hover:bg-yellow-200 px-2 py-1.5 rounded flex items-center gap-1 shadow-sm"
                            data-testid={`app-manage-docs-btn-${app.application_number}`}
                            title="Manage Status & Documents"
                          >
                            <FileText className="w-3.5 h-3.5" /> Action
                          </button>
                          <button
                            onClick={async () => {
                              const uploads = app.uploaded_files ? Object.keys(app.uploaded_files) : (app.uploaded_documents || []);
                              if (!uploads || uploads.length === 0) {
                                toast.warning('આ અરજીમાં કોઈ દસ્તાવેજો અપલોડ થયેલ નથી (No documents found)');
                              }
                              toast.info('પેક તૈયાર થઈ રહ્યું છે... (Building combined PDF...)');
                              try {
                                const res = await axios.get(
                                  `${API_URL}/api/admin/applications/${app.application_number}/pack`,
                                  { responseType: 'blob', headers: token ? { Authorization: `Bearer ${token}` } : {} }
                                );
                                const clientName = (app.full_name || app.user_phone || 'Client').replace(/[^A-Za-z0-9\u0A80-\u0AFF_-]+/g, '_') || 'Client';
                                const filename = `${app.application_number}_${clientName}_Complete_Application.pdf`;
                                const blob = new Blob([res.data], { type: 'application/pdf' });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = filename;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                setTimeout(() => URL.revokeObjectURL(url), 4000);
                                toast.success('પેક તૈયાર (Combined PDF downloaded)');
                              } catch (err) {
                                console.error(err);
                                toast.error('પેક બનાવવામાં નિષ્ફળ (Failed to build combined PDF)');
                              }
                            }}
                            className="text-xs font-bold text-white bg-[#172554] hover:bg-[#0f1a3a] px-2 py-1.5 rounded flex items-center gap-1 shadow-sm"
                            data-testid={`app-download-pack-btn-${app.application_number}`}
                            title="Download Combined Application PDF"
                          >
                            <Download className="w-3.5 h-3.5" /> Pack
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`શું તમે ચોક્કસ ${app.application_number} કાઢી નાખવા માંગો છો? (Are you sure you want to delete this application?)`)) {
                                handleDelete('admin/applications', app.application_number);
                              }
                            }}
                            className="text-red-500 hover:bg-red-50 p-1.5 rounded transition-colors"
                            data-testid={`booking-delete-${app.application_number}`}
                            title="Delete Application"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          {(() => {
                            const visit = adminVisits.find(v => v.application_number === app.application_number);
                            if (visit) {
                              return (
                                <button
                                  onClick={() => setActiveTab('visits')}
                                  className="text-green-600 hover:bg-green-50 p-1.5 rounded transition-colors"
                                  title="View Visit"
                                >
                                  <CalendarCheck className="w-4 h-4" />
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </td>
                      </tr>
                      
                      {/* Fields Summary Dropdown Row */}
                      {showFieldsSummary === app.application_number && (
                        <tr className="bg-blue-50/50">
                          <td colSpan="6" className="p-4">
                            <div className="bg-white border border-blue-100 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                              <h4 className="font-bold text-[#172554] text-gujarati mb-3">અરજી ફોર્મની વિગતો (Form Details Summary)</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.entries(app.form_data || {}).map(([k, v]) => (
                                  <div key={k} className="flex flex-col border-b border-gray-100 pb-2">
                                    <span className="text-xs font-bold text-[#64748B] mb-1">{k}</span>
                                    <span className="text-sm font-medium text-[#172033] break-all">{v}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Manage Documents / Upload Row */}
                      {expandedApp === app.application_number && (
                        <tr className="bg-yellow-50">
                          <td colSpan="6" className="p-4">
                            <div className="bg-white border border-yellow-200 rounded-lg p-4">
                              <h4 className="font-bold text-[#172554] text-gujarati mb-3 flex items-center gap-2">
                                <FileText className="w-4 h-4" /> પ્રોસેસ કરેલા દસ્તાવેજો (Processed Documents)
                              </h4>

                              {app.status !== 'Complete' && (
                                <p className="text-xs text-[#64748B] mb-3 italic">
                                  Note: You can still upload documents, but users only see them once status is set to <b>Complete</b>.
                                </p>
                              )}

                              <div className="flex flex-col gap-2 mb-4">
                                {(app.processed_documents || []).length === 0 ? (
                                  <p className="text-sm text-[#64748B] text-gujarati">કોઈ દસ્તાવેજ અપલોડ થયો નથી.</p>
                                ) : (
                                  (app.processed_documents || []).map(doc => (
                                    <div key={doc.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-2">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <FileText className="w-4 h-4 text-[#172554] shrink-0" />
                                        <span className="text-sm font-medium truncate">{doc.original_filename}</span>
                                        <span className="text-xs text-[#64748B]">({Math.round((doc.size || 0)/1024)} KB)</span>
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <a
                                          href={`${API_URL}/api/files/${doc.storage_path}`}
                                          target="_blank" rel="noreferrer"
                                          className="text-[#172554] p-1 hover:bg-gray-200 rounded"
                                          data-testid={`processed-doc-view-${doc.id}`}
                                        >
                                          <Download className="w-4 h-4" />
                                        </a>
                                        <button
                                          onClick={() => handleDeleteProcessedDoc(app.application_number, doc.id)}
                                          className="text-red-500 p-1 hover:bg-red-50 rounded"
                                          data-testid={`processed-doc-delete-${doc.id}`}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>

                              <label className="inline-flex items-center gap-2 cursor-pointer bg-[#F5B800] text-[#172554] px-4 py-2 rounded font-bold hover:bg-[#FFC928] transition-colors">
                                <Upload className="w-4 h-4" />
                                {uploadingApp === app.application_number ? 'Uploading...' : 'Upload Document'}
                                <input
                                  type="file"
                                  className="hidden"
                                  disabled={uploadingApp === app.application_number}
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleUploadProcessedDoc(app.application_number, f);
                                    e.target.value = '';
                                  }}
                                  data-testid={`processed-doc-upload-${app.application_number}`}
                                />
                              </label>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                    })}
                    {filteredApps.length === 0 && (
                      <tr>
                        <td colSpan="9" className="p-8 text-center text-[#64748B] font-medium" data-testid="booking-empty">
                          {hasActiveFilter ? (
                            <>No applications match the current filters. <button onClick={clearFilters} className="text-[#172554] underline font-bold">Clear filters</button></>
                          ) : 'No applications found.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <AdminPagination page={appPage} pageSize={APP_PAGE_SIZE} total={appTotalCount} onChange={setAppPage} />
            </div>
            </div>
            );
          })()}

          {activeTab === 'users' && (() => {
            const formatDate = (iso) => {
              if (!iso) return '-';
              try {
                const d = new Date(iso);
                if (isNaN(d.getTime())) return '-';
                return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
              } catch { return '-'; }
            };
            const sortedUsers = [...(users || [])].sort((a, b) => {
              const ta = a.joined_at ? new Date(a.joined_at).getTime() : 0;
              const tb = b.joined_at ? new Date(b.joined_at).getTime() : 0;
              return usersSortDir === 'desc' ? (tb - ta) : (ta - tb);
            });
            const filteredUsers = sortedUsers.filter(u => {
              if (!usersSearch.trim()) return true;
              const q = usersSearch.trim().toLowerCase();
              return (
                (u.name || '').toLowerCase().includes(q) ||
                (u.phone || '').toLowerCase().includes(q) ||
                (u.email || '').toLowerCase().includes(q)
              );
            });
            const exportRows = filteredUsers.map((u, idx) => ({
              'Sr No': idx + 1,
              'User ID': u.id || '',
              'Name': u.name || '',
              'Mobile Number': u.phone || '',
              'City': u.city || '',
              'Email ID': u.email || '',
              'Joined Date': formatDate(u.joined_at),
              'Successful Applications': u.successful_applications || 0,
            }));
            return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="users-management">
              <div className="flex flex-wrap gap-2 justify-between items-center mb-4 border-b pb-2">
                <h3 className="text-lg font-bold text-[#172554]">Registered Users · {filteredUsers.length}{usersSearch.trim() ? <span className="text-xs font-normal text-[#64748B] ml-2">of {sortedUsers.length}</span> : null}</h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setUsersSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                    className="bg-[#172554] hover:bg-[#1e3a8a] text-white px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1"
                    data-testid="users-sort-joined-date"
                    title="Sort by Joined Date"
                  >
                    Joined {usersSortDir === 'desc' ? '↓ Newest' : '↑ Oldest'}
                  </button>
                  <button
                    onClick={() => handleExportExcel('Users', exportRows)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors"
                    data-testid="users-export-excel"
                  >
                    Export Excel
                  </button>
                  <button
                    onClick={() => handleExportPDF('Users', exportRows, [
                      { key: 'Sr No', label: 'Sr No' },
                      { key: 'User ID', label: 'User ID' },
                      { key: 'Name', label: 'Name' },
                      { key: 'Mobile Number', label: 'Mobile' },
                      { key: 'City', label: 'City' },
                      { key: 'Email ID', label: 'Email' },
                      { key: 'Joined Date', label: 'Joined Date' },
                      { key: 'Successful Applications', label: 'Successful Apps' },
                    ])}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors"
                    data-testid="users-export-pdf"
                  >
                    Export PDF
                  </button>
                </div>
              </div>

              {/* Search: name / mobile / email — jump to any user in one keystroke */}
              <div className="mb-4 relative max-w-lg">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <input
                  type="text"
                  value={usersSearch}
                  onChange={(e) => setUsersSearch(e.target.value)}
                  placeholder="Search by name, mobile or email…"
                  className="w-full pl-9 pr-9 py-2 text-sm border border-[#E2E8F0] bg-gray-50 rounded-lg focus:ring-1 focus:ring-[#172554] focus:bg-white outline-none"
                  data-testid="users-search-input"
                />
                {usersSearch && (
                  <button
                    onClick={() => setUsersSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200"
                    data-testid="users-search-clear"
                    title="Clear search"
                  >
                    <X className="w-3.5 h-3.5 text-[#64748B]" />
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[900px]">
                  <thead>
                    <tr className="bg-gray-50 text-[#64748B] text-xs uppercase tracking-wider">
                      <th className="p-3 border-b font-semibold">Sr No</th>
                      <th className="p-3 border-b font-semibold">User ID</th>
                      <th className="p-3 border-b font-semibold">Name</th>
                      <th className="p-3 border-b font-semibold">Mobile</th>
                      <th className="p-3 border-b font-semibold">City</th>
                      <th className="p-3 border-b font-semibold">Email</th>
                      <th
                        className="p-3 border-b font-semibold cursor-pointer select-none hover:text-[#172554]"
                        onClick={() => setUsersSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                        title="Click to toggle sort"
                      >
                        Joined Date {usersSortDir === 'desc' ? '↓' : '↑'}
                      </th>
                      <th className="p-3 border-b font-semibold text-center">Successful Applications</th>
                      <th className="p-3 border-b font-semibold text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-[#172033] divide-y divide-gray-100">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="p-6 text-center text-[#64748B]">
                          {usersSearch.trim()
                            ? `કોઈ યુઝર મળ્યો નથી "${usersSearch}" માટે (No user matches your search).`
                            : 'કોઈ નોંધાયેલ યુઝર્સ મળ્યા નથી (No registered users yet).'}
                        </td>
                      </tr>
                    ) : filteredUsers.map((u, idx) => (
                      <tr key={u.id || `${u.phone}-${idx}`} className="hover:bg-gray-50 transition-colors" data-testid={`user-row-${u.phone || idx}`}>
                        <td className="p-3 font-mono text-xs text-[#64748B]">{idx + 1}</td>
                        <td className="p-3 font-mono text-[11px] text-[#64748B]" title={u.id}>{(u.id || '').slice(-6) || '-'}</td>
                        <td className="p-3 font-bold text-gujarati">{u.name || '-'}</td>
                        <td className="p-3 font-mono text-xs">{u.phone || '-'}</td>
                        <td className="p-3 text-gujarati">{u.city || '-'}</td>
                        <td className="p-3 text-xs text-[#334155]">{u.email || '-'}</td>
                        <td className="p-3 text-xs">{formatDate(u.joined_at)}</td>
                        <td className="p-3 text-center">
                          <button
                            type="button"
                            onClick={() => {
                              if (!u.phone) return;
                              setBookingPhoneFilter(u.phone);
                              setBookingTypeFilter('all');
                              setBookingStatusFilter('Complete');
                              setBookingDateFrom('');
                              setBookingDateTo('');
                              setActiveTab('bookings');
                            }}
                            disabled={!u.phone}
                            className={`inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-extrabold transition-all ${u.successful_applications > 0 ? 'bg-green-100 text-green-700 border border-green-200 hover:bg-green-200 hover:ring-2 hover:ring-green-300 cursor-pointer' : 'bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed opacity-70'}`}
                            data-testid={`user-completed-${u.phone || idx}`}
                            title={u.successful_applications > 0 ? `આ યુઝરની Complete બુકિંગ્સ જુઓ (View this user's completed bookings)` : 'No successful applications yet'}
                          >
                            {u.successful_applications || 0}
                            {u.successful_applications > 0 && <ChevronRight className="w-3 h-3 ml-0.5" />}
                          </button>
                        </td>
                        <td className="p-3">
                          <button
                            onClick={() => {
                              if (window.confirm(`શું તમે ચોક્કસ આ યુઝરને કાઢી નાખવા માંગો છો? (Are you sure you want to delete this user?)`)) {
                                handleDelete('admin/users', u.id);
                              }
                            }}
                            className="text-red-500 hover:bg-red-50 p-1.5 rounded transition-colors"
                            title="Delete User"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}

          {activeTab === 'accounts' && (() => {
            // Apply monthly filter (YYYY-MM) if set
            const inMonth = (d) => {
              if (!accountsMonthFilter) return true;
              if (!d) return false;
              try {
                const dt = new Date(d);
                const ym = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
                return ym === accountsMonthFilter;
              } catch { return false; }
            };
            const filtered = (accounts || []).filter(a => inMonth(a.date));
            const incomeEntries = filtered.filter(a => (a.type || '').toLowerCase() === 'income' || a.type === 'Credit');
            const expenseEntries = filtered.filter(a => (a.type || '').toLowerCase() === 'expense' || a.type === 'Debit');
            const totalIncome = incomeEntries.reduce((s, a) => s + Number(a.amount || 0), 0);
            const totalExpense = expenseEntries.reduce((s, a) => s + Number(a.amount || 0), 0);
            const netRevenue = totalIncome - totalExpense;
            const fmtDate = (d) => {
              if (!d) return '';
              try { return new Date(d).toLocaleDateString('en-GB'); } catch { return d; }
            };
            const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

            return (
              <div className="space-y-6" data-testid="account-book-tab">
                {/* Summary Widgets */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl shadow-sm border-l-4 border-green-500 p-5 flex items-center gap-4" data-testid="widget-total-income">
                    <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
                      <span className="text-green-600 font-bold text-xl">↑</span>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">Total Income (કુલ આવક)</div>
                      <div className="text-2xl font-extrabold text-green-600">{fmtINR(totalIncome)}</div>
                      <div className="text-xs text-[#64748B]">{incomeEntries.length} entries</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border-l-4 border-red-500 p-5 flex items-center gap-4" data-testid="widget-total-expenses">
                    <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center">
                      <span className="text-red-600 font-bold text-xl">↓</span>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">Total Expenses (કુલ ખર્ચ)</div>
                      <div className="text-2xl font-extrabold text-red-600">{fmtINR(totalExpense)}</div>
                      <div className="text-xs text-[#64748B]">{expenseEntries.length} entries</div>
                    </div>
                  </div>
                  <div className={`rounded-xl shadow-sm p-5 flex items-center gap-4 ${netRevenue >= 0 ? 'bg-[#172554] text-white' : 'bg-red-600 text-white'}`} data-testid="widget-net-revenue">
                    <div className="w-12 h-12 rounded-lg bg-[#F5B800] flex items-center justify-center">
                      <span className="text-[#172554] font-extrabold text-xl">₹</span>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider font-semibold opacity-90">Net Revenue (ચોખ્ખી આવક)</div>
                      <div className="text-2xl font-extrabold text-[#F5B800]">{fmtINR(netRevenue)}</div>
                      <div className="text-xs opacity-80">Income − Expenses</div>
                    </div>
                  </div>
                </div>

                {/* Export bar + Monthly Filter */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-wrap justify-between items-center gap-3">
                  <h3 className="text-lg font-bold text-[#172554]">Account Book — Income vs Expenses</h3>
                  <div className="flex flex-wrap gap-2 items-center">
                    <button
                      data-testid="accounts-sync-btn"
                      onClick={async () => {
                        try {
                          toast.info('આવક સુધારી રહ્યા છીએ...');
                          const r = await axios.post(`${API_URL}/api/admin/accounts/backfill`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          const [acc, gc] = await Promise.all([
                            axios.get(`${API_URL}/api/admin/accounts`, { headers: { Authorization: `Bearer ${token}` } }),
                            axios.get(`${API_URL}/api/admin/greencards`, { headers: { Authorization: `Bearer ${token}` } })
                          ]);
                          setAccounts(acc.data);
                          setGreencards(gc.data);
                          toast.success(`સુધારેલ: ${r.data.portal_fixed}, નવી ગ્રીન કાર્ડ એન્ટ્રી: ${r.data.greencards_recovered || r.data.greencard_added}`);
                        } catch (e) { toast.error('Sync નિષ્ફળ'); }
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-3 py-1.5 rounded text-gujarati"
                    >આવક સુધારો (Sync)</button>
                    <label className="text-xs font-bold text-[#64748B] uppercase tracking-wider">Month:</label>
                    <input
                      type="month"
                      value={accountsMonthFilter}
                      onChange={e => setAccountsMonthFilter(e.target.value)}
                      className="border border-gray-300 rounded p-1.5 text-xs outline-none focus:border-[#172554]"
                      data-testid="accounts-month-filter"
                    />
                    {accountsMonthFilter && (
                      <button
                        onClick={() => setAccountsMonthFilter('')}
                        className="text-xs font-bold text-[#172554] underline hover:text-blue-800"
                        data-testid="accounts-clear-month-filter"
                      >
                        Clear
                      </button>
                    )}
                    <button onClick={() => handleExportExcel('Accounts', filtered)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors" data-testid="export-accounts-excel">Export Excel</button>
                    <button onClick={() => handleExportPDF('Accounts', filtered, [
                      {key: 'date', label: 'Date'},
                      {key: 'type', label: 'Type'},
                      {key: 'tag', label: 'Tag'},
                      {key: 'description', label: 'Description'},
                      {key: 'user_name', label: 'User'},
                      {key: 'application_number', label: 'App No'},
                      {key: 'amount', label: 'Amount'}
                    ])} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors" data-testid="export-accounts-pdf">Export PDF</button>
                  </div>
                </div>

                {/* 2-Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Income Column */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="income-column">
                    <div className="bg-[#172554] px-5 py-3 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#F5B800]"></span>
                        <h4 className="text-white font-bold tracking-wide">આવક (Income) — Portal Payments</h4>
                      </div>
                      <span className="text-[#F5B800] font-extrabold">{fmtINR(totalIncome)}</span>
                    </div>
                    <div className="max-h-[520px] overflow-y-auto">
                      {incomeEntries.length === 0 ? (
                        <div className="p-8 text-center text-[#64748B] text-sm">
                          <div className="text-4xl mb-2 opacity-30">↑</div>
                          હજુ સુધી કોઈ પોર્ટલ ચુકવણી પ્રાપ્ત થઈ નથી
                        </div>
                      ) : (
                        <table className="w-full text-left border-collapse text-sm">
                          <thead>
                            <tr className="bg-[#F5B800]/10 text-[#172554] text-xs uppercase tracking-wider">
                              <th className="p-3 font-semibold">Date</th>
                              <th className="p-3 font-semibold">Details</th>
                              <th className="p-3 font-semibold text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {incomeEntries.map(a => (
                              <tr key={a.id} className="hover:bg-green-50/40 transition-colors" data-testid={`income-row-${a.id}`}>
                                <td className="p-3 text-xs text-[#64748B] whitespace-nowrap">{fmtDate(a.date)}</td>
                                <td className="p-3">
                                  <div className="font-medium text-[#172033] text-gujarati">{a.description || a.sub_category_name || '—'}</div>
                                  <div className="text-xs text-[#64748B] flex items-center gap-2 mt-0.5 flex-wrap">
                                    {a.application_number && <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{a.application_number}</span>}
                                    {a.user_name && <span className="text-gujarati">{a.user_name}</span>}
                                    <span className="inline-block px-1.5 py-0.5 rounded bg-[#172554] text-[#F5B800] text-[10px] font-bold uppercase">{a.tag || 'Portal'}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-right font-bold text-green-600 whitespace-nowrap">+{fmtINR(a.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  {/* Expenses Column */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="expense-column">
                    <div className="bg-[#172554] px-5 py-3 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#F5B800]"></span>
                        <h4 className="text-white font-bold tracking-wide">ખર્ચ (Expenses) — Manual Entries</h4>
                      </div>
                      <span className="text-[#F5B800] font-extrabold">{fmtINR(totalExpense)}</span>
                    </div>

                    {/* Add Expense Form */}
                    <div className="bg-[#F5B800]/5 p-4 border-b border-gray-100">
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <input
                          type="text"
                          placeholder="વર્ણન (Description)"
                          className="border border-gray-300 rounded p-2 text-sm outline-none focus:border-[#172554] col-span-2 text-gujarati"
                          value={expenseForm.description}
                          onChange={e => setExpenseForm({...expenseForm, description: e.target.value})}
                          data-testid="expense-description-input"
                        />
                        <input
                          type="number"
                          placeholder="રકમ (Amount ₹)"
                          className="border border-gray-300 rounded p-2 text-sm outline-none focus:border-[#172554]"
                          value={expenseForm.amount}
                          onChange={e => setExpenseForm({...expenseForm, amount: e.target.value})}
                          data-testid="expense-amount-input"
                        />
                        <select
                          className="border border-gray-300 rounded p-2 text-sm outline-none focus:border-[#172554] bg-white"
                          value={expenseForm.tag}
                          onChange={e => setExpenseForm({...expenseForm, tag: e.target.value})}
                          data-testid="expense-tag-select"
                        >
                          <option value="Manual">Manual</option>
                          <option value="Salary">Salary</option>
                          <option value="Rent">Rent</option>
                          <option value="Utility">Utility</option>
                          <option value="Marketing">Marketing</option>
                          <option value="Other">Other</option>
                        </select>
                        <input
                          type="date"
                          className="border border-gray-300 rounded p-2 text-sm outline-none focus:border-[#172554] col-span-2"
                          value={expenseForm.date}
                          onChange={e => setExpenseForm({...expenseForm, date: e.target.value})}
                          data-testid="expense-date-input"
                        />
                      </div>
                      <button
                        onClick={handleAddExpense}
                        className="w-full bg-[#F5B800] text-[#172554] font-bold py-2 rounded hover:bg-[#FFC928] transition-colors text-sm"
                        data-testid="add-expense-btn"
                      >
                        + ખર્ચ ઉમેરો (Add Expense)
                      </button>
                    </div>

                    <div className="max-h-[380px] overflow-y-auto">
                      {expenseEntries.length === 0 ? (
                        <div className="p-8 text-center text-[#64748B] text-sm">
                          <div className="text-4xl mb-2 opacity-30">↓</div>
                          કોઈ ખર્ચ નોંધાયેલ નથી
                        </div>
                      ) : (
                        <table className="w-full text-left border-collapse text-sm">
                          <thead>
                            <tr className="bg-[#F5B800]/10 text-[#172554] text-xs uppercase tracking-wider">
                              <th className="p-3 font-semibold">Date</th>
                              <th className="p-3 font-semibold">Details</th>
                              <th className="p-3 font-semibold text-right">Amount</th>
                              <th className="p-3 font-semibold w-8"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {expenseEntries.map(a => (
                              <tr key={a.id} className="hover:bg-red-50/40 transition-colors" data-testid={`expense-row-${a.id}`}>
                                <td className="p-3 text-xs text-[#64748B] whitespace-nowrap">{fmtDate(a.date)}</td>
                                <td className="p-3">
                                  <div className="font-medium text-[#172033] text-gujarati">{a.description || '—'}</div>
                                  <div className="text-xs text-[#64748B] mt-0.5">
                                    <span className="inline-block px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold uppercase">{a.tag || 'Manual'}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-right font-bold text-red-600 whitespace-nowrap">−{fmtINR(a.amount)}</td>
                                <td className="p-3 text-right">
                                  <button
                                    onClick={() => handleDeleteAccountEntry(a.id)}
                                    className="text-red-400 hover:text-red-600 transition-colors"
                                    title="Delete"
                                    data-testid={`delete-expense-${a.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {activeTab === 'insights' && (() => {
            const d = insightsData || { total_visitors: 0, mobile_visitors: 0, desktop_visitors: 0, daily_series: [] };
            const pieData = [
              { name: 'Mobile (મોબાઈલ)', value: d.mobile_visitors || 0 },
              { name: 'Desktop (ડેસ્કટોપ)', value: d.desktop_visitors || 0 },
            ];
            const NAVY = '#172554';
            const YELLOW = '#F5B800';
            const PIE_COLORS = [YELLOW, NAVY];
            const shortDate = (s) => {
              try {
                const parts = s.split('-');
                return `${parts[2]}/${parts[1]}`;
              } catch { return s; }
            };
            const rangeButtons = [
              { id: 'today', label: 'Today' },
              { id: '7d', label: 'Last 7 Days' },
              { id: '30d', label: 'Last 30 Days' },
              { id: 'all', label: 'All Time' },
            ];
            return (
              <div className="space-y-6" data-testid="insights-tab">
                {/* Filter bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-wrap justify-between items-center gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-[#172554] flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#F5B800]" /> Visitor Insights (મુલાકાતી અંતર્દૃષ્ટિ)
                    </h3>
                    <p className="text-xs text-[#64748B] mt-0.5">Home page visits, aggregated per day by device.</p>
                  </div>
                  <div className="inline-flex bg-gray-100 rounded-lg p-1 gap-1">
                    {rangeButtons.map(b => (
                      <button
                        key={b.id}
                        onClick={() => setInsightsRange(b.id)}
                        className={`px-3 py-1.5 text-xs font-bold rounded transition-colors ${insightsRange === b.id ? 'bg-[#172554] text-white shadow' : 'text-[#64748B] hover:text-[#172554]'}`}
                        data-testid={`insights-range-${b.id}`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Key metric widgets */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-[#172554] rounded-xl shadow-sm p-5 flex items-center gap-4 text-white relative overflow-hidden" data-testid="widget-total-visitors">
                    {typeof d.change_pct === 'number' && insightsRange !== 'all' && (
                      <span
                        className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${d.change_pct >= 0 ? 'bg-green-500/25 text-green-300 ring-1 ring-green-400/40' : 'bg-red-500/25 text-red-300 ring-1 ring-red-400/40'}`}
                        title={`vs previous ${insightsRange}`}
                        data-testid="widget-change-badge"
                      >
                        {d.change_pct >= 0 ? '▲' : '▼'} {Math.abs(d.change_pct)}%
                      </span>
                    )}
                    <div className="w-12 h-12 rounded-lg bg-[#F5B800] flex items-center justify-center shrink-0">
                      <Users className="w-6 h-6 text-[#172554]" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#F5B800] font-bold">Total Visitors</div>
                      <div className="text-3xl font-extrabold text-white">{d.total_visitors.toLocaleString('en-IN')}</div>
                      <div className="text-[11px] text-white/70">
                        કુલ મુલાકાતીઓ
                        {typeof d.previous_total === 'number' && (
                          <span className="ml-2 text-white/50">vs {d.previous_total.toLocaleString('en-IN')} prev</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border-l-4 border-[#F5B800] p-5 flex items-center gap-4" data-testid="widget-mobile-visitors">
                    <div className="w-12 h-12 rounded-lg bg-[#F5B800]/15 flex items-center justify-center">
                      <Smartphone className="w-6 h-6 text-[#F5B800]" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">Mobile Visitors</div>
                      <div className="text-3xl font-extrabold text-[#172554]">{d.mobile_visitors.toLocaleString('en-IN')}</div>
                      <div className="text-[11px] text-[#64748B]">
                        {d.total_visitors > 0 ? `${Math.round((d.mobile_visitors / d.total_visitors) * 100)}%` : '0%'} of total
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border-l-4 border-[#172554] p-5 flex items-center gap-4" data-testid="widget-desktop-visitors">
                    <div className="w-12 h-12 rounded-lg bg-[#172554]/10 flex items-center justify-center">
                      <Monitor className="w-6 h-6 text-[#172554]" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">Desktop Visitors</div>
                      <div className="text-3xl font-extrabold text-[#172554]">{d.desktop_visitors.toLocaleString('en-IN')}</div>
                      <div className="text-[11px] text-[#64748B]">
                        {d.total_visitors > 0 ? `${Math.round((d.desktop_visitors / d.total_visitors) * 100)}%` : '0%'} of total
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charts row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Bar chart: Daily visitors */}
                  <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="daily-visitors-chart">
                    <div className="bg-[#172554] px-5 py-3 flex justify-between items-center">
                      <h4 className="text-white font-bold tracking-wide">Daily Visitors (દૈનિક મુલાકાતીઓ)</h4>
                      <span className="text-[#F5B800] text-xs font-bold uppercase">{insightsRange === 'today' ? 'Today' : insightsRange === '7d' ? 'Last 7 Days' : insightsRange === '30d' ? 'Last 30 Days' : 'All Time'}</span>
                    </div>
                    <div className="p-4 h-[340px]">
                      {insightsLoading ? (
                        <div className="h-full flex items-center justify-center text-[#64748B] text-sm">Loading…</div>
                      ) : (d.daily_series || []).length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-[#64748B] text-sm gap-2">
                          <TrendingUp className="w-10 h-10 opacity-30" />
                          <p>આ સમયગાળામાં કોઈ મુલાકાત નોંધાયેલ નથી</p>
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={(d.daily_series || []).map(r => ({ ...r, dateShort: shortDate(r.date) }))} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="dateShort" stroke="#64748B" fontSize={11} interval="preserveStartEnd" />
                            <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                            <Tooltip
                              contentStyle={{ backgroundColor: NAVY, border: 'none', borderRadius: 6, color: '#fff', fontSize: 12 }}
                              itemStyle={{ color: '#fff' }}
                              labelStyle={{ color: YELLOW, fontWeight: 700 }}
                              cursor={{ fill: 'rgba(245,184,0,0.08)' }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                            <Bar dataKey="mobile" stackId="a" fill={YELLOW} name="Mobile" radius={[0, 0, 0, 0]} />
                            <Bar dataKey="desktop" stackId="a" fill={NAVY} name="Desktop" radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>

                  {/* Pie chart: Device split */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="device-split-chart">
                    <div className="bg-[#172554] px-5 py-3">
                      <h4 className="text-white font-bold tracking-wide">Device Split (ડિવાઈસ પ્રમાણ)</h4>
                    </div>
                    <div className="p-4 h-[340px]">
                      {d.total_visitors === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-[#64748B] text-sm gap-2">
                          <Smartphone className="w-10 h-10 opacity-30" />
                          <p>ડેટા ઉપલબ્ધ નથી</p>
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={pieData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={55}
                              outerRadius={95}
                              paddingAngle={3}
                              label={({ percent }) => `${Math.round(percent * 100)}%`}
                              labelLine={false}
                              stroke="#fff"
                              strokeWidth={3}
                            >
                              {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                            </Pie>
                            <Tooltip
                              contentStyle={{ backgroundColor: NAVY, border: 'none', borderRadius: 6, color: '#fff', fontSize: 12 }}
                              itemStyle={{ color: '#fff' }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>

                {/* Gujarat Cities Heatmap */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="city-heatmap-chart">
                  <div className="bg-[#172554] px-5 py-3 flex justify-between items-center">
                    <h4 className="text-white font-bold tracking-wide flex items-center gap-2">
                      <span className="text-[#F5B800]">🇮🇳</span>
                      Gujarat Cities Heatmap (ગુજરાત શહેરો)
                    </h4>
                    <span className="text-[#F5B800] text-xs font-bold uppercase">Top {Math.min((d.by_city || []).length, 10)}</span>
                  </div>
                  <div className="p-4">
                    {(() => {
                      const cities = (d.by_city || []).slice(0, 10);
                      if (cities.length === 0) {
                        return (
                          <div className="h-[220px] flex flex-col items-center justify-center text-[#64748B] text-sm gap-2">
                            <span className="text-4xl opacity-20">📍</span>
                            <p>હજુ કોઈ શહેર ડેટા ઉપલબ્ધ નથી</p>
                            <p className="text-[11px] text-[#64748B]">યુઝર્સ પ્રોફાઈલમાં શહેર પસંદ કરે એટલે અહીં દેખાશે.</p>
                          </div>
                        );
                      }
                      const maxCount = Math.max(...cities.map(c => c.count));
                      const intensity = (c) => {
                        const ratio = c.count / maxCount;
                        if (ratio > 0.75) return 'bg-[#172554] text-white';
                        if (ratio > 0.5) return 'bg-[#172554]/80 text-white';
                        if (ratio > 0.25) return 'bg-[#F5B800] text-[#172554]';
                        return 'bg-[#F5B800]/40 text-[#172554]';
                      };
                      return (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Bar chart on left */}
                          <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={cities} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                                <XAxis type="number" stroke="#64748B" fontSize={11} allowDecimals={false} />
                                <YAxis type="category" dataKey="city" stroke="#172554" fontSize={11} width={100} tick={{ fontWeight: 700 }} />
                                <Tooltip
                                  contentStyle={{ backgroundColor: NAVY, border: 'none', borderRadius: 6, color: '#fff', fontSize: 12 }}
                                  itemStyle={{ color: '#fff' }}
                                  labelStyle={{ color: YELLOW, fontWeight: 700 }}
                                  cursor={{ fill: 'rgba(23,37,84,0.06)' }}
                                />
                                <Bar dataKey="count" name="Visitors" radius={[0, 6, 6, 0]}>
                                  {cities.map((c, i) => (
                                    <Cell key={i} fill={c.count / maxCount > 0.5 ? NAVY : YELLOW} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          {/* Heatmap tiles on right */}
                          <div className="flex flex-col justify-center gap-2" data-testid="city-heatmap-tiles">
                            <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Heatmap Tiles</div>
                            <div className="grid grid-cols-2 gap-2">
                              {cities.map((c, i) => (
                                <div
                                  key={i}
                                  className={`${intensity(c)} rounded-lg px-3 py-2.5 shadow-sm flex items-center justify-between transition-transform hover:scale-[1.02]`}
                                  data-testid={`heatmap-tile-${i}`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-xs opacity-70">📍</span>
                                    <span className="font-bold text-gujarati text-sm truncate">{c.city}</span>
                                  </div>
                                  <span className="font-extrabold text-base ml-2 shrink-0">{c.count.toLocaleString('en-IN')}</span>
                                </div>
                              ))}
                            </div>
                            <div className="text-[10px] text-[#64748B] mt-2 flex items-center gap-2">
                              <span>Intensity:</span>
                              <span className="inline-block w-3 h-3 rounded bg-[#F5B800]/40"></span>
                              <span>Low</span>
                              <span className="inline-block w-3 h-3 rounded bg-[#F5B800]"></span>
                              <span>Mid</span>
                              <span className="inline-block w-3 h-3 rounded bg-[#172554]/80"></span>
                              <span>High</span>
                              <span className="inline-block w-3 h-3 rounded bg-[#172554]"></span>
                              <span>Top</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })()}

          {activeTab === 'executives' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="executives-tab">
              <h3 className="text-lg font-bold mb-4 text-[#172554] border-b pb-2 flex items-center gap-2">
                <UserCog className="w-5 h-5" /> Manage Executives (સ્ટાફ મેનેજમેન્ટ)
              </h3>

              {/* Add / Edit form */}
              <div className="bg-[#F5B800]/5 border border-[#F5B800]/30 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Name (નામ)"
                    className="border border-gray-300 rounded p-2.5 text-sm outline-none focus:border-[#172554] text-gujarati"
                    value={execForm.name}
                    onChange={e => setExecForm({...execForm, name: e.target.value})}
                    data-testid="exec-name-input"
                  />
                  <input
                    type="text"
                    placeholder="Designation (હોદ્દો)"
                    className="border border-gray-300 rounded p-2.5 text-sm outline-none focus:border-[#172554] text-gujarati"
                    value={execForm.designation}
                    onChange={e => setExecForm({...execForm, designation: e.target.value})}
                    data-testid="exec-designation-input"
                  />
                  <input
                    type="tel"
                    placeholder="Contact Number (સંપર્ક)"
                    className="border border-gray-300 rounded p-2.5 text-sm outline-none focus:border-[#172554]"
                    value={execForm.contact_number}
                    onChange={e => setExecForm({...execForm, contact_number: e.target.value})}
                    data-testid="exec-contact-input"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAddOrUpdateExec}
                    className="bg-[#172554] text-white font-bold px-5 py-2 rounded hover:bg-[#0f1a3a] transition-colors text-sm"
                    data-testid="exec-save-btn"
                  >
                    {editingExec ? 'Update Executive' : '+ Add Executive'}
                  </button>
                  {editingExec && (
                    <button
                      onClick={() => { setEditingExec(null); setExecForm({ name: '', designation: '', contact_number: '' }); }}
                      className="border border-gray-300 text-[#64748B] font-bold px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* Workload Card Grid */}
              {(executives || []).length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center text-[#64748B] text-gujarati" data-testid="exec-empty-state">
                  કોઈ એક્ઝિક્યુટિવ ઉમેર્યા નથી
                </div>
              ) : (() => {
                const OVERLOAD_THRESHOLD = 5;
                const workload = (execId) => ({ total: 0, active: 0, inprocess: 0, complete: 0, ...((appStats.exec_workload || {})[execId] || {}) });
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exec-workload-grid">
                    {executives.map(ex => {
                      const w = workload(ex.id);
                      const overloaded = w.active >= OVERLOAD_THRESHOLD;
                      const initials = (ex.name || '?').trim().charAt(0).toUpperCase();
                      return (
                        <div
                          key={ex.id}
                          className={`relative bg-white rounded-xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${overloaded ? 'border-red-400 ring-2 ring-red-100' : 'border-gray-200'}`}
                          data-testid={`exec-card-${ex.id}`}
                        >
                          {/* Overload badge */}
                          {overloaded && (
                            <span
                              className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-red-100 text-red-700 ring-1 ring-red-300"
                              title={`Active workload ≥ ${OVERLOAD_THRESHOLD}`}
                              data-testid={`exec-overload-badge-${ex.id}`}
                            >
                              Overloaded
                            </span>
                          )}

                          {/* Header strip */}
                          <div className="bg-[#172554] px-4 py-3 flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full bg-[#F5B800] text-[#172554] flex items-center justify-center font-extrabold text-lg shrink-0 text-gujarati">
                              {initials}
                            </div>
                            <div className="min-w-0">
                              <div className="text-white font-bold text-sm truncate text-gujarati" title={ex.name}>{ex.name}</div>
                              <div className="text-[#F5B800] text-[11px] font-medium truncate text-gujarati" title={ex.designation}>{ex.designation}</div>
                            </div>
                          </div>

                          {/* Contact */}
                          <div className="px-4 pt-3 flex items-center gap-2 text-xs text-[#64748B]">
                            <Phone className="w-3.5 h-3.5" />
                            <span className="font-mono">{ex.contact_number}</span>
                          </div>

                          {/* Workload stats */}
                          <div className="px-4 py-3 grid grid-cols-3 gap-2" data-testid={`exec-workload-${ex.id}`}>
                            <button
                              onClick={() => { setActiveTab('bookings'); setBookingStatusFilter('all'); }}
                              className="bg-gray-50 hover:bg-[#F5B800]/10 rounded-lg p-2 text-center transition-colors"
                              title="Total assigned bookings"
                            >
                              <div className="text-lg font-extrabold text-[#172554]" data-testid={`exec-total-${ex.id}`}>{w.total}</div>
                              <div className="text-[9px] font-bold uppercase text-[#64748B] tracking-wider">Total</div>
                            </button>
                            <button
                              onClick={() => { setActiveTab('bookings'); setBookingStatusFilter('Inprocess'); }}
                              className={`rounded-lg p-2 text-center transition-colors ${overloaded ? 'bg-red-50 hover:bg-red-100' : 'bg-[#F5B800]/10 hover:bg-[#F5B800]/25'}`}
                              title="Currently active (Submitted / Pending / Inprocess)"
                            >
                              <div className={`text-lg font-extrabold ${overloaded ? 'text-red-700' : 'text-[#172554]'}`} data-testid={`exec-active-${ex.id}`}>{w.active}</div>
                              <div className={`text-[9px] font-bold uppercase tracking-wider ${overloaded ? 'text-red-600' : 'text-[#172554]'}`}>Active</div>
                            </button>
                            <button
                              onClick={() => { setActiveTab('bookings'); setBookingStatusFilter('Complete'); }}
                              className="bg-green-50 hover:bg-green-100 rounded-lg p-2 text-center transition-colors"
                              title="Completed bookings"
                            >
                              <div className="text-lg font-extrabold text-green-700" data-testid={`exec-complete-${ex.id}`}>{w.complete}</div>
                              <div className="text-[9px] font-bold uppercase text-green-700 tracking-wider">Done</div>
                            </button>
                          </div>

                          {/* Capacity bar */}
                          <div className="px-4 pb-3">
                            <div className="flex justify-between text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                              <span>Capacity</span>
                              <span className={overloaded ? 'text-red-600' : ''}>{w.active}/{OVERLOAD_THRESHOLD}</span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all ${overloaded ? 'bg-red-500' : w.active >= Math.ceil(OVERLOAD_THRESHOLD * 0.6) ? 'bg-[#F5B800]' : 'bg-green-500'}`}
                                style={{ width: `${Math.min(100, (w.active / OVERLOAD_THRESHOLD) * 100)}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="px-4 pb-4 flex justify-end gap-1 border-t border-gray-100 pt-3">
                            <button onClick={() => startEditExec(ex)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded" data-testid={`exec-edit-${ex.id}`} title="Edit">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteExec(ex.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded" data-testid={`exec-delete-${ex.id}`} title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'form_master' && (
            <FormMaster />
          )}

          {activeTab === 'cities' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold mb-4 text-[#172554]">Add New City (શહેર)</h3>
              <div className="flex gap-4 mb-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <input type="text" placeholder="City Name (e.g. અમદાવાદ)" className="border border-[#E2E8F0] p-2.5 flex-1 rounded-md outline-none focus:border-blue-500 text-gujarati" value={cityForm.name} onChange={e => setCityForm({...cityForm, name: e.target.value})} />
                <button className="bg-[#F5B800] text-[#172554] px-6 py-2.5 rounded-md font-medium hover:bg-[#FFC928] transition-colors shadow-sm" onClick={() => { handleCreate('cities', cityForm); setCityForm({name: ''}) }}>Save City</button>
              </div>

              <h3 className="text-lg font-bold mb-4 text-[#172554] border-b pb-2">Available Cities</h3>
              <ul className="divide-y divide-gray-100">
                {(cities || []).map(c => (
                  <li key={c.id} className="py-4 flex justify-between items-center hover:bg-gray-50 px-2 rounded transition-colors">
                    <span className="font-medium text-[#172033] text-gujarati text-sm">{c.name}</span>
                    <button className="text-red-500 text-sm font-medium hover:underline" onClick={() => handleDelete('cities', c.id)}>Delete</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'settings' && (
            <SiteSettings />
          )}

          {activeTab === 'blogs' && (
            <ManageBlogs token={token} />
          )}

          {activeTab === 'bulk_emails' && (
            <BulkEmailManager token={token} />
          )}

          {activeTab === 'visits' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-xl font-bold mb-6 text-[#172554] flex items-center gap-2"><CalendarCheck className="w-5 h-5"/> Visit Allocations (Green Card)</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full text-left bg-white border border-gray-200 shadow-sm rounded-lg">
                  <thead className="bg-[#172554] text-white">
                    <tr>
                      <th className="p-3 text-sm font-bold">App #</th>
                      <th className="p-3 text-sm font-bold">Applicant (Name)</th>
                      <th className="p-3 text-sm font-bold">Service</th>
                      <th className="p-3 text-sm font-bold">Scheduled Time</th>
                      <th className="p-3 text-sm font-bold">Status</th>
                      <th className="p-3 text-sm font-bold w-48">Admin Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {adminVisits.map(visit => {
                      const statusMap = {
                        'Pending Schedule': 'bg-gray-100 text-gray-700 border-gray-200',
                        'Scheduled': 'bg-blue-100 text-blue-700 border-blue-200',
                        'Completed': 'bg-green-100 text-green-700 border-green-200',
                        'Cancelled': 'bg-red-100 text-red-700 border-red-200',
                        'Rescheduled': 'bg-orange-100 text-orange-700 border-orange-200',
                        'No Show': 'bg-gray-200 text-gray-800 border-gray-300'
                      };
                      const statusLabels = {
                        'Pending Schedule': 'શેડ્યૂલ બાકી',
                        'Scheduled': 'શેડ્યૂલ થયેલ',
                        'Completed': 'પૂર્ણ થયેલ',
                        'Cancelled': 'રદ કરેલ',
                        'Rescheduled': 'ફરી શેડ્યૂલ',
                        'No Show': 'હાજર નહોતા'
                      };
                      return (
                        <tr key={visit.id || visit._id} className="hover:bg-gray-50">
                          <td className="p-3">
                            <div className="font-bold text-[#172554]">{visit.application_number}</div>
                            <div className="text-xs text-green-700 font-bold mt-1">{visit.card_number}</div>
                          </td>
                          <td className="p-3 font-bold text-gujarati">{visit.full_name}</td>
                          <td className="p-3 font-bold text-gujarati text-sm">{visit.service_name}</td>
                          <td className="p-3">
                            {visit.scheduled_date ? (
                              <>
                                <div className="font-bold text-[#172033]">{new Date(visit.scheduled_date).toLocaleDateString()}</div>
                                <div className="text-xs text-[#64748B] font-bold mt-0.5">{visit.scheduled_time}</div>
                              </>
                            ) : <span className="text-gray-400 text-sm italic">Not Scheduled</span>}
                          </td>
                          <td className="p-3">
                            <select 
                              value={visit.status}
                              onChange={async (e) => {
                                try {
                                  await axios.put(`${API_URL}/api/admin/visits/${visit.id || visit._id}`, { status: e.target.value }, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success('Status updated');
                                  fetchData();
                                } catch(err) { toast.error('Update failed'); }
                              }}
                              className={`border text-xs font-bold rounded p-1.5 outline-none block w-full ${statusMap[visit.status] || ''}`}
                            >
                              <option value="Pending Schedule">શેડ્યૂલ બાકી (Pending)</option>
                              <option value="Scheduled">શેડ્યૂલ થયેલ (Scheduled)</option>
                              <option value="Completed">પૂર્ણ થયેલ (Completed)</option>
                              <option value="Rescheduled">ફરી શેડ્યૂલ (Rescheduled)</option>
                              <option value="Cancelled">રદ કરેલ (Cancelled)</option>
                              <option value="No Show">હાજર નહોતા (No Show)</option>
                            </select>
                          </td>
                          <td className="p-3">
                            <input 
                              type="text" 
                              placeholder="Notes..."
                              className="border border-gray-200 rounded p-1.5 w-full text-xs outline-none focus:border-blue-500"
                              defaultValue={visit.admin_notes || ''}
                              onBlur={async (e) => {
                                if (e.target.value !== (visit.admin_notes || '')) {
                                  try {
                                    await axios.put(`${API_URL}/api/admin/visits/${visit.id || visit._id}`, { status: visit.status, admin_notes: e.target.value }, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success('Notes saved');
                                    fetchData();
                                  } catch(err) { toast.error('Update failed'); }
                                }
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    {adminVisits.length === 0 && (
                      <tr><td colSpan="6" className="p-4 text-center text-gray-500">No visits allocated yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'greencards' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold mb-4 text-[#172554]">Green Card Settings</h3>
              {greencardSettings && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 bg-green-50 p-4 rounded-lg border border-green-200">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Fee Amount (₹)</label>
                    <input type="number" className="border border-green-300 rounded p-2 w-full outline-none focus:border-green-600" value={greencardSettings.fee_amount} onChange={e => setGreencardSettings({...greencardSettings, fee_amount: parseInt(e.target.value)||0})} />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Renewal Period (Years)</label>
                    <input type="number" className="border border-green-300 rounded p-2 w-full outline-none focus:border-green-600" value={greencardSettings.renewal_period_years} onChange={e => setGreencardSettings({...greencardSettings, renewal_period_years: parseInt(e.target.value)||0})} />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1 text-gujarati">ગ્રીન કાર્ડ ડિસ્કાઉન્ટ (%)</label>
                    <input type="number" min="0" max="100" data-testid="gc-settings-discount-input" className="border border-green-300 rounded p-2 w-full outline-none focus:border-green-600" value={greencardSettings.discount_percentage || 0} onChange={e => setGreencardSettings({...greencardSettings, discount_percentage: Math.max(0, Math.min(100, parseInt(e.target.value)||0))})} />
                  </div>
                  <div className="col-span-1 md:col-span-3">
                    <label className="block text-sm font-bold text-gray-700 mb-1">Allowed Cities (comma separated)</label>
                    <input type="text" className="border border-green-300 rounded p-2 w-full outline-none focus:border-green-600 text-gujarati" value={(greencardSettings.allowed_cities||[]).join(', ')} onChange={e => setGreencardSettings({...greencardSettings, allowed_cities: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})} />
                  </div>
                  <div className="col-span-1 md:col-span-3">
                    <label className="block text-sm font-bold text-gray-700 mb-1">Benefits</label>
                    <GreencardBenefitsEditor benefits={greencardSettings.benefits||[]} onChange={b => setGreencardSettings({...greencardSettings, benefits: b})} />
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <button onClick={async () => {
                      try {
                        await axios.put(`${API_URL}/api/greencard/settings`, greencardSettings, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Settings updated');
                        fetchData();
                      } catch(e) { toast.error('Update failed'); }
                    }} data-testid="greencard-settings-save-btn" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded">Save Settings</button>
                  </div>
                </div>
              )}

              {(() => {
                const now = new Date();
                const filtered = gcFiltered;
                const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '';
                const exportRows = filtered.map(c => ({
                  'કાર્ડ નંબર': c.card_number, 'પૂરું નામ': c.full_name, 'મોબાઈલ': c.user_phone, 'શહેર': c.city,
                  'સરનામું': c.address || '', 'પિન કોડ': c.pincode || '', 'કુટુંબ સભ્યો': c.family_members, 'સભ્યોના નામ': (c.family_names || []).join(', '),
                  'સભ્યપદ શરૂ': fmtD(c.valid_from), 'નવીકરણ તારીખ': fmtD(c.valid_until),
                  'સ્થિતિ': new Date(c.valid_until) < now ? 'એક્સપાયર્ડ' : 'સક્રિય', 'રકમ (₹)': c.amount, 'ટ્રાન્ઝેક્શન ID': c.payment_id || '',
                  'કુરિયર સ્થિતિ': COURIER_META[c.courier_status || 'Not Dispatched']?.label || '', 'ટ્રેકિંગ નંબર': c.tracking_number || ''
                }));
                const pdfCols = [
                  { label: 'કાર્ડ નંબર', key: 'card_number' },
                  { label: 'પૂરું નામ', key: 'full_name' },
                  { label: 'મોબાઈલ', key: 'user_phone' },
                  { label: 'શહેર', key: 'city' },
                  { label: 'કુટુંબ', accessor: c => `${c.family_members} સભ્યો` },
                  { label: 'સભ્યપદ શરૂ', accessor: c => fmtD(c.valid_from) },
                  { label: 'નવીકરણ તારીખ', accessor: c => fmtD(c.valid_until) },
                  { label: 'સ્થિતિ', accessor: c => new Date(c.valid_until) < now ? 'એક્સપાયર્ડ' : 'સક્રિય' },
                  { label: 'રકમ', accessor: c => `₹${c.amount || 0}` },
                  { label: 'કુરિયર', accessor: c => COURIER_META[c.courier_status || 'Not Dispatched']?.label || '' },
                ];
                return (
                  <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4 border-b pb-3">
                    <h3 className="text-lg font-bold text-[#172554] text-gujarati shrink-0">Issued Green Cards <span className="text-sm text-gray-500" data-testid="gc-filtered-count">({filtered.length}/{(greencards || []).length})</span></h3>
                    <div className="flex flex-1 flex-wrap gap-2 md:justify-end">
                      <div className="flex w-full justify-end gap-2 mb-1">
                        {gcFiltered.some(c => gcSelected[c.card_number]) ? (
                          <>
                            <span data-testid="gc-selected-count" className="self-center text-xs font-bold text-green-700 text-gujarati">{gcFiltered.filter(c => gcSelected[c.card_number]).length} પસંદ કરેલ</span>
                            <button data-testid="gc-bulk-selected-labels" disabled={bulkDownloading} onClick={() => handleBulkLabels(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-lg text-gujarati border border-gray-300">પસંદ કરેલા લેબલ (Selected Labels)</button>
                            <button data-testid="gc-bulk-selected-cards" disabled={bulkDownloading} onClick={() => handleBulkCards(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-lg text-gujarati border border-gray-300">પસંદ કરેલા કાર્ડ (Selected Cards)</button>
                          </>
                        ) : (
                          <>
                            <button data-testid="gc-bulk-all-labels" disabled={bulkDownloading} onClick={() => handleBulkLabels(true)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-lg text-gujarati border border-gray-300">બધા લેબલ ડાઉનલોડ (Download All Labels)</button>
                            <button data-testid="gc-bulk-all-cards" disabled={bulkDownloading} onClick={() => handleBulkCards(true)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-lg text-gujarati border border-gray-300">બધા કાર્ડ ડાઉનલોડ (Download All Cards)</button>
                          </>
                        )}
                      </div>
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input data-testid="gc-search-input" value={gcSearch} onChange={e => setGcSearch(e.target.value)} placeholder="નામ, મોબાઈલ અથવા કાર્ડ નંબર શોધો..." className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-64 outline-none focus:border-green-600 text-gujarati" />
                      </div>
                      <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm font-bold" data-testid="gc-status-filter">
                        {[['all', 'બધા'], ['active', 'સક્રિય'], ['expired', 'એક્સપાયર્ડ']].map(([k, l]) => (
                          <button key={k} data-testid={`gc-filter-${k}`} onClick={() => setGcStatusFilter(k)} className={`px-3 py-2 text-gujarati transition-colors ${gcStatusFilter === k ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-green-50'}`}>{l}</button>
                        ))}
                      </div>
                      <select data-testid="gc-courier-filter" value={gcCourierFilter} onChange={e => setGcCourierFilter(e.target.value)} className="border border-gray-300 rounded-lg text-sm font-bold px-3 py-2 outline-none focus:border-green-600 text-gujarati bg-white">
                        <option value="all">કુરિયર: બધા ({(greencards || []).length})</option>
                        {Object.entries(COURIER_META).map(([k, m]) => (
                          <option key={k} value={k}>{m.label} ({(greencards || []).filter(c => (c.courier_status || 'Not Dispatched') === k).length})</option>
                        ))}
                      </select>
                      <button data-testid="gc-export-excel" onClick={() => handleExportExcel('GreenCards', exportRows)} className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-bold px-3 py-2 rounded-lg text-gujarati"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
                      <button data-testid="gc-export-pdf" onClick={() => handleExportPDF('GreenCards', filtered, pdfCols)} className="flex items-center gap-1.5 bg-[#172554] hover:bg-blue-900 text-white text-sm font-bold px-3 py-2 rounded-lg text-gujarati"><FileText className="w-4 h-4" /> PDF</button>
                    </div>
                  </div>
                );
              })()}
              <div className="overflow-x-auto">
                <table className="w-full text-left bg-white border border-gray-200">
                  <thead className="bg-[#172554] text-white">
                    <tr>
                      <th className="p-3 text-sm font-bold w-10 text-center">
                        <input data-testid="gc-select-all-checkbox" type="checkbox" onChange={e => {
                          const checked = e.target.checked;
                          const ns = {};
                          gcFiltered.forEach(c => ns[c.card_number] = checked);
                          setGcSelected(ns);
                        }} checked={gcFiltered.length > 0 && gcFiltered.every(c => gcSelected[c.card_number])} />
                      </th>
                      <th className="p-3 text-sm font-bold">Card Number</th>
                      <th className="p-3 text-sm font-bold">Name</th>
                      <th className="p-3 text-sm font-bold">Phone</th>
                      <th className="p-3 text-sm font-bold">City</th>
                      <th className="p-3 text-sm font-bold">Valid Until</th>
                      <th className="p-3 text-sm font-bold">Status</th>
                      <th className="p-3 text-sm font-bold text-gujarati">કુરિયર સ્થિતિ (Courier Status)</th>
                      <th className="p-3 text-sm font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(gcFiltered).length === 0 && (
                      <tr><td colSpan={9} className="p-6 text-center text-gray-500 text-gujarati" data-testid="gc-no-results">કોઈ કાર્ડ મળ્યું નથી</td></tr>
                    )}
                    {(gcFiltered).map(card => {
                      const isExpired = new Date(card.valid_until) < new Date();
                      return (
                        <tr key={card.id} className="hover:bg-gray-50">
                          <td className="p-3 text-center">
                            <input data-testid={`gc-row-checkbox-${card.card_number}`} type="checkbox" checked={!!gcSelected[card.card_number]} onChange={e => setGcSelected({...gcSelected, [card.card_number]: e.target.checked})} />
                          </td>
                          <td className="p-3 font-bold text-green-700">{card.card_number}</td>
                          <td className="p-3 font-bold text-gujarati uppercase">{card.full_name}</td>
                          <td className="p-3">{card.user_phone}</td>
                          <td className="p-3 text-gujarati">{card.city}</td>
                          <td className={`p-3 font-bold ${isExpired ? 'text-red-500' : 'text-gray-700'}`}>
                            {new Date(card.valid_until).toLocaleDateString()}
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 text-xs font-bold rounded-full ${isExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {isExpired ? 'Expired' : 'Active'}
                            </span>
                          </td>
                          <td className="p-3">
                            <CourierCell card={card} token={token} onUpdated={u => setGreencards(prev => prev.map(c => c.card_number === u.card_number ? u : c))} />
                          </td>
                          <td className="p-3">
                            <div className="flex flex-col gap-2">
                            <button
                              data-testid={`gc-pdf-${card.card_number}`}
                              onClick={async () => {
                                try {
                                  toast.info('Downloading PDF...');
                                  const res = await axios.get(`${API_URL}/api/admin/greencards/${card.card_number}/pdf`, {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `GreenCard_${card.card_number}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.parentNode.removeChild(link);
                                } catch(e) {
                                  toast.error('Failed to download PDF');
                                }
                              }}
                              className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold py-1.5 px-3 rounded flex items-center gap-1 transition-colors w-full"
                            >
                              <FileText className="w-3.5 h-3.5" /> PDF જુઓ
                            </button>
                            <button onClick={() => setGcViewCard(card)} data-testid={`gc-view-card-${card.card_number}`} className="text-xs bg-green-50 text-green-700 hover:bg-green-100 font-bold py-1.5 px-3 rounded flex items-center gap-1 transition-colors text-gujarati w-full">
                              <CreditCard className="w-3.5 h-3.5" /> કાર્ડ જુઓ (View Card)
                            </button>
                            <button onClick={() => setGcDownloadCard(card)} disabled={!!gcDownloadCard} data-testid={`gc-download-card-${card.card_number}`} className="text-xs bg-[#172554] text-white hover:bg-blue-900 disabled:opacity-60 font-bold py-1.5 px-3 rounded flex items-center gap-1 transition-colors text-gujarati w-full">
                              <Download className="w-3.5 h-3.5" /> ડાઉનલોડ (Download)
                            </button>
                            <button
                              data-testid={`gc-welcome-${card.card_number}`}
                              onClick={async () => {
                                try {
                                  toast.info('Downloading Welcome Letter...');
                                  const res = await axios.get(`${API_URL}/api/admin/greencards/${card.card_number}/welcome`, {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `WelcomeLetter_${card.card_number}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.parentNode.removeChild(link);
                                } catch(e) {
                                  toast.error('Failed to download welcome letter');
                                }
                              }}
                              className="text-xs bg-green-50 text-green-700 hover:bg-green-100 font-bold py-1.5 px-3 rounded flex items-center gap-1 transition-colors text-gujarati w-full"
                            >
                              <FileText className="w-3.5 h-3.5" /> સ્વાગત પત્ર (Welcome Letter)
                            </button>
                            <button
                              data-testid={`gc-label-${card.card_number}`}
                              onClick={async () => {
                                try {
                                  toast.info('Downloading Delivery Label...');
                                  const res = await axios.get(`${API_URL}/api/admin/greencards/${card.card_number}/label`, {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `DeliveryLabel_${card.card_number}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.parentNode.removeChild(link);
                                } catch(e) {
                                  toast.error('Failed to download delivery label');
                                }
                              }}
                              className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 font-bold py-1.5 px-3 rounded flex items-center gap-1 transition-colors text-gujarati w-full"
                            >
                              <FileText className="w-3.5 h-3.5" /> ડિલિવરી લેબલ પ્રિન્ટ (Print Delivery Label)
                            </button>
                            <button onClick={() => setGcEditCard({...card, family_names: card.family_names || Array.from({length: card.family_members||0}, ()=>'')})} data-testid={`gc-edit-card-${card.card_number}`} className="text-xs bg-yellow-50 text-yellow-700 hover:bg-yellow-100 font-bold py-1.5 px-3 rounded flex items-center gap-1 transition-colors text-gujarati w-full">
                              <Edit className="w-3.5 h-3.5" /> સંપાદિત કરો (Edit)
                            </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {gcEditCard && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 overflow-y-auto" onClick={() => !gcEditSaving && setGcEditCard(null)} data-testid="gc-edit-modal">
                  <div className="bg-white rounded-2xl p-6 shadow-2xl flex flex-col gap-4 max-w-[600px] w-full my-auto" onClick={e => e.stopPropagation()}>
                    <div className="w-full flex justify-between items-center border-b pb-3">
                      <h3 className="font-bold text-[#172554] text-gujarati">ગ્રીન કાર્ડ સંપાદિત કરો - {gcEditCard.card_number}</h3>
                      <button onClick={() => !gcEditSaving && setGcEditCard(null)} data-testid="gc-edit-modal-close" className="text-gray-500 hover:text-gray-800" disabled={gcEditSaving}><X className="w-5 h-5" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-xs font-bold text-gray-700 mb-1 text-gujarati">પૂરું નામ (Full Name)</label>
                        <input data-testid="gc-edit-name" value={gcEditCard.full_name||''} onChange={e => setGcEditCard({...gcEditCard, full_name: e.target.value})} className="w-full border rounded p-2 text-sm text-gujarati outline-none focus:border-green-600 uppercase" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1 text-gujarati">જન્મ તારીખ (DOB)</label>
                        <input type="date" data-testid="gc-edit-dob" value={gcEditCard.dob||''} onChange={e => setGcEditCard({...gcEditCard, dob: e.target.value})} className="w-full border rounded p-2 text-sm outline-none focus:border-green-600" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1 text-gujarati">મોબાઈલ (Mobile)</label>
                        <input data-testid="gc-edit-phone" value={gcEditCard.user_phone||''} onChange={e => setGcEditCard({...gcEditCard, user_phone: e.target.value})} className="w-full border rounded p-2 text-sm outline-none focus:border-green-600" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1 text-gujarati">શહેર (City)</label>
                        <input data-testid="gc-edit-city" value={gcEditCard.city||''} onChange={e => setGcEditCard({...gcEditCard, city: e.target.value})} className="w-full border rounded p-2 text-sm text-gujarati outline-none focus:border-green-600" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1 text-gujarati">રાજ્ય (State)</label>
                        <input data-testid="gc-edit-state" value={gcEditCard.state||''} onChange={e => setGcEditCard({...gcEditCard, state: e.target.value})} className="w-full border rounded p-2 text-sm text-gujarati outline-none focus:border-green-600" />
                      </div>
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-xs font-bold text-gray-700 mb-1 text-gujarati">સંપર્ક સરનામું (Address)</label>
                        <textarea data-testid="gc-edit-address" value={gcEditCard.address||''} onChange={e => setGcEditCard({...gcEditCard, address: e.target.value})} className="w-full border rounded p-2 text-sm text-gujarati outline-none focus:border-green-600 min-h-[60px]" />
                      </div>
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-xs font-bold text-gray-700 mb-1 text-gujarati">કુટુંબના સભ્યોની સંખ્યા (Family Members)</label>
                        <input type="number" min="1" max="15" data-testid="gc-edit-family-count" value={gcEditCard.family_members||1} onChange={e => {
                          const num = parseInt(e.target.value) || 1;
                          let fNames = [...(gcEditCard.family_names || [])];
                          if (num > fNames.length) {
                             fNames = [...fNames, ...Array(num - fNames.length).fill('')];
                          } else {
                             fNames = fNames.slice(0, num);
                          }
                          setGcEditCard({...gcEditCard, family_members: num, family_names: fNames});
                        }} className="w-full border rounded p-2 text-sm outline-none focus:border-green-600" />
                      </div>
                      {gcEditCard.family_names && gcEditCard.family_names.map((n, i) => (
                        <div key={i} className="col-span-1 md:col-span-2">
                          <label className="block text-xs font-bold text-gray-700 mb-1 text-gujarati">સભ્ય {i+1} નું નામ (Member {i+1} Name)</label>
                          <input data-testid={`gc-edit-family-name-${i}`} value={n||''} onChange={e => {
                            const arr = [...gcEditCard.family_names];
                            arr[i] = e.target.value;
                            setGcEditCard({...gcEditCard, family_names: arr});
                          }} className="w-full border rounded p-2 text-sm text-gujarati outline-none focus:border-green-600" />
                        </div>
                      ))}
                      <div className="col-span-1 md:col-span-2 border-t pt-3 mt-2">
                        <label className="block text-xs font-bold text-[#172554] mb-2 text-gujarati">ઉપલબ્ધ દસ્તાવેજો (Available Documents)</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {[
                            { key: 'has_pan', label: 'પાન કાર્ડ (PAN)' },
                            { key: 'has_aadhaar', label: 'આધાર કાર્ડ (Aadhaar)' },
                            { key: 'has_ration', label: 'રેશન કાર્ડ (Ration)' },
                            { key: 'has_election', label: 'ચૂંટણી કાર્ડ (Voter ID)' },
                            { key: 'has_property', label: 'મિલકત વેરો (Property)' },
                            { key: 'has_agriculture', label: 'કૃષિ/જમીન (Agriculture)' },
                          ].map(d => (
                            <label key={d.key} className="flex items-center gap-2 text-sm text-gujarati cursor-pointer">
                              <input type="checkbox" data-testid={`gc-edit-doc-${d.key}`} checked={!!gcEditCard[d.key]} onChange={e => setGcEditCard({...gcEditCard, [d.key]: e.target.checked})} className="w-4 h-4 text-green-600 accent-green-600" />
                              {d.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="w-full flex justify-end gap-3 pt-3 border-t">
                      <button onClick={() => setGcEditCard(null)} disabled={gcEditSaving} data-testid="gc-edit-cancel" className="px-4 py-2 rounded font-bold text-gray-600 hover:bg-gray-100 text-gujarati disabled:opacity-50">રદ કરો (Cancel)</button>
                      <button 
                        onClick={async () => {
                          setGcEditSaving(true);
                          try {
                            const res = await axios.put(`${API_URL}/api/admin/greencards/${gcEditCard.card_number}`, gcEditCard, { headers: { Authorization: `Bearer ${token}` } });
                            setGreencards(prev => prev.map(c => c.card_number === res.data.card_number ? res.data : c));
                            setGcEditCard(null);
                            toast.success('કાર્ડ અપડેટ થઈ ગયું (Card Updated)');
                          } catch (e) {
                            toast.error('અપડેટ નિષ્ફળ (Update Failed)');
                          } finally {
                            setGcEditSaving(false);
                          }
                        }}
                        disabled={gcEditSaving}
                        data-testid="gc-edit-save" 
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-bold text-gujarati flex items-center gap-2 disabled:opacity-70"
                      >
                        {gcEditSaving ? 'સેવિંગ...' : 'સેવ કરો (Save)'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {gcViewCard && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setGcViewCard(null)} data-testid="gc-view-modal">
                  <div className="bg-white rounded-2xl p-6 shadow-2xl flex flex-col items-center gap-4 max-w-[480px] w-full" onClick={e => e.stopPropagation()}>
                    <div className="w-full flex justify-between items-center">
                      <h3 className="font-bold text-[#172554] text-gujarati">ગ્રીન કાર્ડ - {gcViewCard.card_number}</h3>
                      <button onClick={() => setGcViewCard(null)} data-testid="gc-view-modal-close" className="text-gray-500 hover:text-gray-800"><X className="w-5 h-5" /></button>
                    </div>
                    <GreenCardVisual ref={gcViewRef} card={gcViewCard} />
                    <button
                      data-testid="gc-view-modal-download"
                      onClick={() => downloadCardPng(gcViewRef.current, gcViewCard.card_number).then(() => toast.success('કાર્ડ ડાઉનલોડ થઈ ગયું (Card downloaded)')).catch(() => toast.error('કાર્ડ ડાઉનલોડ કરવામાં ભૂલ'))}
                      className="w-full bg-[#172554] hover:bg-blue-900 text-white font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 text-gujarati"
                    >
                      <Download className="w-4 h-4" /> ડાઉનલોડ (Download)
                    </button>
                    <button
                      data-testid="gc-view-modal-print"
                      onClick={() => printCardAtAadhaarSize(gcViewRef.current, gcViewCard.card_number).catch(() => toast.error('પ્રિન્ટ કરવામાં ભૂલ'))}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 text-gujarati"
                    >
                      <FileText className="w-4 h-4" /> પ્રિન્ટ કરો (Print Card)
                    </button>
                  </div>
                </div>
              )}
              {gcDownloadCard && (
                <div style={{ position: 'fixed', left: '-10000px', top: 0 }} aria-hidden="true">
                  <GreenCardVisual ref={gcDownloadRef} card={gcDownloadCard} />
                </div>
              )}
            </div>
          )}

        </main>
      </div>

      {/* Category Edit Modal */}
      {assignExecModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="assign-exec-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative">
            <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={() => setAssignExecModal(null)}>
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold text-[#172554] mb-1 flex items-center gap-2">
              <UserCog className="w-5 h-5" /> Assign Executive
            </h3>
            <p className="text-xs text-[#64748B] text-gujarati mb-5">
              અરજી <b className="font-mono">{assignExecModal.appNumber}</b> માટે એક્ઝિક્યુટિવ પસંદ કરો.
            </p>
            <label className="block text-sm font-bold text-[#172033] mb-2">Executive (એક્ઝિક્યુટિવ)</label>
            <select
              value={assignSelectedExec}
              onChange={e => setAssignSelectedExec(e.target.value)}
              className="w-full border border-gray-300 rounded p-2.5 text-sm outline-none focus:border-[#172554] bg-white mb-5"
              data-testid="assign-exec-select"
            >
              {(executives || []).map(ex => (
                <option key={ex.id} value={ex.id}>
                  {ex.name} — {ex.designation} ({ex.contact_number})
                </option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setAssignExecModal(null)}
                className="border border-gray-300 text-[#64748B] font-bold px-4 py-2 rounded hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmAssignExecutive}
                className="bg-[#F5B800] text-[#172554] font-bold px-5 py-2 rounded hover:bg-[#FFC928] text-sm"
                data-testid="assign-exec-confirm"
              >
                Assign & Move to In-Process
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Edit Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative">
            <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={() => setEditingCategory(null)}>
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold text-[#172554] mb-4">Edit Main Category</h3>
            <form onSubmit={handleEditCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input 
                  type="text" required
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500 text-gujarati"
                  value={editingCategory.title || ''}
                  onChange={(e) => setEditingCategory({...editingCategory, title: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icon Type</label>
                <input 
                  type="text" required
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                  value={editingCategory.icon_type || ''}
                  onChange={(e) => setEditingCategory({...editingCategory, icon_type: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <input 
                  type="number" required
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                  value={editingCategory.order_num || 0}
                  onChange={(e) => setEditingCategory({...editingCategory, order_num: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount % (છૂટ)</label>
                <input 
                  type="number" min="0" max="100"
                  data-testid="edit-cat-discount-input"
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                  value={editingCategory.discount_percentage || 0}
                  onChange={(e) => setEditingCategory({...editingCategory, discount_percentage: Math.max(0, Math.min(100, parseInt(e.target.value) || 0))})}
                />
              </div>
              <div className="pt-2">
                <button type="submit" className="w-full bg-[#172554] text-white py-2.5 rounded-lg font-bold hover:bg-blue-900 transition-colors">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sub Category Edit Modal */}
      {editingSubCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative">
            <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={() => setEditingSubCategory(null)}>
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold text-[#172554] mb-4">Edit Sub Category</h3>
            <form onSubmit={handleEditSubCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category</label>
                <select 
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500 text-gujarati" 
                  value={editingSubCategory.category_name || ''} 
                  onChange={e => setEditingSubCategory({...editingSubCategory, category_name: e.target.value})}
                >
                  {(categories || []).map(c => <option key={c.id} value={c.title}>{c.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input 
                  type="text" required
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500 text-gujarati"
                  value={editingSubCategory.title || ''}
                  onChange={(e) => setEditingSubCategory({...editingSubCategory, title: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Fee (₹)</label>
                <input 
                  type="number" required min="0"
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                  value={editingSubCategory.fee_amount || 0}
                  onChange={(e) => setEditingSubCategory({...editingSubCategory, fee_amount: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lucide Icon Type</label>
                <input 
                  type="text" required
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                  value={editingSubCategory.icon_type || ''}
                  onChange={(e) => setEditingSubCategory({...editingSubCategory, icon_type: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <input 
                  type="number" required
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                  value={editingSubCategory.order_num || 0}
                  onChange={(e) => setEditingSubCategory({...editingSubCategory, order_num: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Days (સામાન્ય SLA)</label>
                <input
                  type="text"
                  placeholder="e.g. 3-5 દિવસ"
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500 text-gujarati"
                  value={editingSubCategory.estimated_days || ''}
                  onChange={(e) => setEditingSubCategory({ ...editingSubCategory, estimated_days: e.target.value })}
                  data-testid="edit-subcat-estimated-days"
                />
              </div>
              <div className="bg-orange-50/60 border border-orange-200 rounded p-3">
                <label className="text-xs font-bold text-[#172554] mb-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-[#F5B800]"
                    checked={!!editingSubCategory.tatkal_enabled}
                    onChange={(e) => setEditingSubCategory({ ...editingSubCategory, tatkal_enabled: e.target.checked })}
                    data-testid="edit-subcat-tatkal-enabled"
                  />
                  તાત્કાલિક સેવા સક્ષમ કરો (Enable Tatkal)
                </label>
                <label className="block text-xs font-medium text-gray-700 mb-1 mt-2">Tatkal Fee (₹)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 150"
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500 disabled:bg-gray-100"
                  value={editingSubCategory.tatkal_fee_amount ?? 0}
                  onChange={(e) => setEditingSubCategory({ ...editingSubCategory, tatkal_fee_amount: parseInt(e.target.value) || 0 })}
                  disabled={!editingSubCategory.tatkal_enabled}
                  data-testid="edit-subcat-tatkal-fee"
                />
              </div>
              <div className="bg-yellow-50/60 border border-yellow-200 rounded p-3">
                <label className="text-xs font-bold text-[#172554] mb-1.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5B800]"></span>
                  મહત્વની નોંધ (Important Note)
                </label>
                <textarea
                  rows={4}
                  placeholder="યુઝરને Preview પર બતાવવા માટે નોંધ લખો. ખાલી રાખો તો કંઈ નહીં દેખાય."
                  className="w-full border border-yellow-300 rounded-md p-2.5 text-sm outline-none focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] bg-white text-gujarati resize-y"
                  value={editingSubCategory.important_note || ''}
                  onChange={(e) => setEditingSubCategory({...editingSubCategory, important_note: e.target.value})}
                  data-testid="edit-subcat-important-note"
                />
              </div>
              <div className="pt-2">
                <button type="submit" className="w-full bg-[#172554] text-white py-2.5 rounded-lg font-bold hover:bg-blue-900 transition-colors">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
function GreencardBenefitsEditor({ benefits, onChange }) {
  const [text, setText] = useState('');
  const add = () => {
    const v = text.trim();
    if (!v) return;
    onChange([...benefits, v]);
    setText('');
  };
  return (
    <div data-testid="greencard-benefits-editor">
      <div className="flex gap-2 mb-2">
        <input type="text" data-testid="greencard-benefit-input" className="border border-green-300 rounded p-2 flex-1 outline-none focus:border-green-600 text-gujarati" placeholder="નવો ફાયદો લખો અને Enter દબાવો" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" data-testid="greencard-benefit-add-btn" onClick={add} className="bg-green-600 hover:bg-green-700 text-white font-bold px-4 rounded">ઉમેરો (Add)</button>
      </div>
      <ul className="space-y-1">
        {benefits.map((b, i) => (
          <li key={i} data-testid={`greencard-benefit-item-${i}`} className="flex items-center justify-between bg-white border border-green-200 rounded px-3 py-1.5 text-sm text-gujarati">
            <span>{b}</span>
            <button type="button" data-testid={`greencard-benefit-delete-${i}`} onClick={() => onChange(benefits.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700 font-bold px-2" aria-label="delete">×</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GreenCardSummary({ cards, onOpen }) {
  const now = new Date();
  const expired = cards.filter(c => new Date(c.valid_until) < now).length;
  const newThisMonth = cards.filter(c => {
    const d = new Date(c.valid_from);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const stats = [
    { key: 'total', label: 'કુલ કાર્ડ', value: cards.length },
    { key: 'active', label: 'સક્રિય સભ્યો', value: cards.length - expired },
    { key: 'expired', label: 'એક્સપાયર્ડ', value: expired },
    { key: 'new', label: 'આ મહિને નવા', value: newThisMonth },
  ];
  return (
    <div onClick={onOpen} data-testid="dashboard-greencard-summary" className="md:col-span-2 lg:col-span-4 cursor-pointer bg-gradient-to-r from-green-700 to-green-600 rounded-xl shadow-sm border border-green-500 p-5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-transform">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center"><CreditCard className="w-5 h-5" /></div>
          <h3 className="font-bold text-lg text-gujarati">ગ્રીન કાર્ડ ઝડપી પ્રવેશ <span className="text-green-100 text-sm font-medium">(Green Card Quick Access)</span></h3>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid="dashboard-greencard-view-all" className="text-sm font-bold bg-white text-green-700 hover:bg-green-50 px-3 py-1.5 rounded-lg flex items-center gap-1 text-gujarati">
          બધા જુઓ (View All) <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map(s => (
          <div key={s.key} className="bg-white/10 rounded-lg p-3 border border-white/20">
            <div className="text-2xl font-extrabold" data-testid={`gc-summary-${s.key}`}>{s.value}</div>
            <div className="text-xs text-green-100 font-semibold text-gujarati">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const COURIER_META = {
  'Not Dispatched': { label: 'મોકલ્યું નથી', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  'Dispatched': { label: 'મોકલ્યું', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  'Delivered': { label: 'ડિલિવર થયું', cls: 'bg-green-100 text-green-700 border-green-300' },
  'Returned': { label: 'પરત આવ્યું', cls: 'bg-red-100 text-red-700 border-red-300' },
};

function CourierCell({ card, token, onUpdated }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const status = card.courier_status || 'Not Dispatched';
  const [sel, setSel] = useState(status);
  const [tracking, setTracking] = useState(card.tracking_number || '');
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const meta = COURIER_META[status] || COURIER_META['Not Dispatched'];
  const cn = card.card_number;

  const save = async (newStatus, trk) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/admin/greencards/${cn}/courier`, { status: newStatus, tracking_number: trk }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdated(res.data);
      setSel(res.data.courier_status);
      toast.success('કુરિયર સ્થિતિ અપડેટ થઈ');
    } catch (e) {
      toast.error('કુરિયર સ્થિતિ અપડેટ નિષ્ફળ');
    } finally { setSaving(false); }
  };

  const dirty = sel !== status || (sel === 'Dispatched' && tracking !== (card.tracking_number || ''));

  return (
    <div className="flex flex-col gap-1.5 min-w-[190px]" data-testid={`gc-courier-cell-${cn}`}>
      <span
        data-testid={`gc-courier-badge-${cn}`}
        title={status === 'Dispatched' && card.tracking_number ? `ટ્રેકિંગ નંબર: ${card.tracking_number}` : (card.delivered_at && status === 'Delivered' ? `ડિલિવરી તારીખ: ${new Date(card.delivered_at).toLocaleDateString('en-GB')}` : meta.label)}
        className={`w-fit px-2 py-0.5 text-xs font-bold rounded-full border text-gujarati cursor-help ${meta.cls}`}
      >{meta.label}</span>
      <div className="flex gap-1">
        <select data-testid={`gc-courier-select-${cn}`} value={sel} onChange={e => setSel(e.target.value)} className="border border-gray-300 rounded text-xs p-1 text-gujarati outline-none focus:border-green-600">
          {Object.entries(COURIER_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        {dirty && (
          <button data-testid={`gc-courier-save-${cn}`} disabled={saving} onClick={() => save(sel, sel === 'Dispatched' ? tracking : undefined)} className="text-xs bg-[#172554] text-white px-2 rounded font-bold disabled:opacity-60 text-gujarati">સેવ</button>
        )}
      </div>
      {sel === 'Dispatched' && (
        <input data-testid={`gc-courier-tracking-${cn}`} value={tracking} onChange={e => setTracking(e.target.value)} placeholder="ટ્રેકિંગ નંબર" className="border border-blue-300 rounded text-xs p-1 outline-none focus:border-blue-600 text-gujarati" />
      )}
      {status !== 'Delivered' && (
        <button data-testid={`gc-courier-mark-delivered-${cn}`} disabled={saving} onClick={() => setConfirmOpen(true)} className="w-fit text-xs bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-2 rounded text-gujarati disabled:opacity-60">ડિલિવર થયું તરીકે ચિહ્નિત કરો</button>
      )}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setConfirmOpen(false)} data-testid={`gc-courier-confirm-modal-${cn}`}>
          <div className="bg-white rounded-xl p-6 shadow-2xl max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h4 className="font-bold text-[#172554] mb-2 text-gujarati">ડિલિવરીની પુષ્ટિ કરો</h4>
            <p className="text-sm text-gray-600 mb-4 text-gujarati">શું તમે ખાતરી કરો છો કે કાર્ડ {cn} ડિલિવર થઈ ગયું છે?</p>
            <div className="flex justify-end gap-2">
              <button data-testid={`gc-courier-confirm-cancel-${cn}`} onClick={() => setConfirmOpen(false)} className="px-4 py-2 text-sm rounded bg-gray-100 hover:bg-gray-200 font-bold text-gujarati">રદ કરો</button>
              <button data-testid={`gc-courier-confirm-yes-${cn}`} onClick={async () => { setConfirmOpen(false); await save('Delivered'); }} className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-bold text-gujarati">હા, ડિલિવર થયું</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
